import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { productDraftApiService } from "../../../../service/admin/productDraftApiService";
import { useLoader } from "../../../../base/context/LoaderProvider";
import {
  ToastTypes,
  useMessage,
} from "../../../../base/context/MessageProvider";
import WebButton from "../../../../component/library/webButton";
import WebInput from "../../../../component/library/webInput";
import WebTextArea from "../../../../component/library/webTextArea";
import WebDropdown from "../../../../component/library/webDropdown";
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import "./style.css";
import AlibabaImage from "../../../../component/common/AlibabaImage";
import { normalizeUrl, handleImgError } from "../../../../utils/imageUtils";
import VariantPriceModal from "./VariantPriceModal";
import { applyBulkPricing, syncVariantPricing } from "./variantPricing";
import PriceLogPanel from "./PriceLogPanel";
import { productListApiService } from "../../../../service/admin/productApiService";
import AIModelService from "../../../../service/admin/aiModelService";
import { adminApiService } from "../../../../service/admin/adminApiService";
import { fetchToken } from "../../../../lib/globalApiClient";

// Ensure description HTML renders in production with external images
const transformDescriptionForProd = (html) => {
  if (!html) return "";
  let safe = String(html);
  // Normalize http→https for image sources
  safe = safe.replace(/(<img\b[^>]*?src\s*=\s*")(http:)([^">]+)("[^>]*>)/gi, ($0, p1, _p2, p3, p4) => `${p1}https:${p3}${p4}`);
  // Inject referrerpolicy and crossorigin to all img tags if missing
  safe = safe.replace(/<img\b([^>]*)>/gi, (m, attrs) => {
    let a = attrs;
    if (!/referrerpolicy=/i.test(a)) a += ' referrerpolicy="no-referrer"';
    if (!/crossorigin=/i.test(a)) a += ' crossorigin="anonymous"';
    return `<img${a}>`;
  });
  return safe;
};

// Sanitize Alibaba-specific markup so it renders inside editors (strip maps/areas/json blocks)
const transformDescriptionForEditor = (html) => {
  if (!html) return "";
  let out = String(html);
  // Drop hidden dynamic offer json blocks
  out = out.replace(/<div[^>]*class=["']sdmap-dynamic-offer-list["'][\s\S]*?<\/div>/gi, "");
  // Remove map/area constructs that Quill strips anyway
  out = out.replace(/<map[\s\S]*?<\/map>/gi, "");
  out = out.replace(/<area[^>]*>/gi, "");
  // Remove usemap attribute on images
  out = out.replace(/\susemap=\"[^\"]*\"/gi, "");
  // Normalize image srcs to https
  out = out.replace(/(<img\b[^>]*?src\s*=\s*")(http:)([^">]+)("[^>]*>)/gi, ($0, p1, _p2, p3, p4) => `${p1}https:${p3}${p4}`);
  return out;
};

export default function EditProductDraft() {
  const { id } = useParams();
  const navigate = useNavigate();
  const loader = useLoader();
  const message = useMessage();

  const [categoryOptions, setCategoryOptions] = useState([]);
  const [originalCategories, setOriginalCategories] = useState([]);
  const [currentCategoryOptions, setCurrentCategoryOptions] = useState([]);
  const [selectedCategoryPath, setSelectedCategoryPath] = useState([]);
  const [selectedCategoryFullPath, setSelectedCategoryFullPath] = useState([]);
  const [categoriesLoaded, setCategoriesLoaded] = useState(false);
  const [variantSelection, setVariantSelection] = useState({});

  // Price Range breakdown (right-side drawer)
  const [priceRangeCalcOpen, setPriceRangeCalcOpen] = useState(false);
  const [priceRangeCalcMeta, setPriceRangeCalcMeta] = useState(null); // { index, ap }
  const [priceRangeCalcResult, setPriceRangeCalcResult] = useState(null); // { algorithm, breakdown }
  const [priceRangeCalcLoading, setPriceRangeCalcLoading] = useState(false);
  const [priceRangeCalcError, setPriceRangeCalcError] = useState("");

  const [draft, setDraft] = useState({
    // Basic Information
    title: "",
    sku: "",
    parentSku: "",
    brand: "",
    manufacturer: "",
    description: "",
    model: "",
    part_number: "",



    // Pricing
    vendor_price: "",
    sp: "",
    mrp: "",
    consign_price: "",

    // Inventory
    quantity: "",
    handling_days: "",
    max_order_quantity: "",

    local_shipping: "",

    // Product Details
    length: "",
    width: "",
    height: "",
    weight: "",
    warranty: "",
    gst: "",
    hsn: "",

    // Images
    imgUrl: [],

    // Variations
    variations: [],

    // Price Ranges (for PDP display when variant details not available)
    priceRanges: [],

    // Location & Status
    location: "",
    status: "",
    is_display: "",
    is_delete: false,

    // Categories & Tags
    categoryId: "",
    catalogId: [],
    tags: [],
    channel: [],

    // Product Specific Fields
    feed_product_type: "",
    care_instructions: "",
    bottoms_body_type: "",
    target_gender: "",
    age_range_description: "",
    bottoms_size_system: "",
    bottoms_size_class: "",
    bottoms_size: "",
    bullet_points: [],
    generic_keywords: "",
    style_name: "",
    packer_contact_information: "",
    item_type_name: "",
    occasion_type: [],
    duration_unit_of_measure: "",
    sport_type: "",
    color_map: "",
    size_map: "",
    pattern_type: "",
    material_type: "",
    duration: "",
    rise_style: "",
    lifestyle: "",
    number_of_pockets: "",
    manufacturer_contact_information: "",
    fit_type: "",
    pocket_description: "",
    department_name: "",
    unit_count: "",
    item_length_description: "",
    unit_count_type: "",
    country_of_origin: "",
    fabric_type: "",
    fulfillment_latency: "",
    number_of_items: "",

    // Draft Specific Fields
    offerId: "",
    source: "",
    processingStatus: "",
    originalData: {},

    // AI Generated Content
    aiGenerated: {
      title: "",
      description: "",
      keywords: "",
      bullet_points: [],
      generatedAt: new Date()
    },

    // Search Terms
    search_terms: [],
    target_audience: [],

    // Legacy fields for backward compatibility
    item_name: "",
    item_sku: "",
    brand_name: "",
    product_description: "",
    standard_price: "",
    sale_price: "",
    main_image_url: "",
    other_image_url1: "",
    other_image_url2: "",
    other_image_url3: "",
    other_image_url4: "",
    other_image_url5: "",
    other_image_url6: "",
    other_image_url7: "",
    other_image_url8: "",
    bullet_point1: "",
    bullet_point2: "",
    bullet_point3: "",
    bullet_point4: "",
    bullet_point5: "",
    condition_type: "",
    condition_note: "",
    currency: "",
    offering_can_be_giftwrapped: false,
    offering_can_be_gift_messaged: false,
    recommended_browse_nodes: "",
  });

  const [loading, setLoading] = useState(false);
  const [editorReady, setEditorReady] = useState(false);
  const editorRef = useRef(null);
  const formRef = useRef(null);
  const contentScrollRef = useRef(null)
  const [activeSection, setActiveSection] = useState("basic-info");
  const [collapsed, setCollapsed] = useState({});
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const sectionIds = [
    "basic-info",
    "pricing-inventory",
    // "dimensions-weight",
    "images",
    "product-details",
    "seo-keywords",
    "draft-info",
  ];

  // Scroll detection for active section (RIGHT container only)
  useEffect(() => {
    const scrollEl = contentScrollRef.current;
    if (!scrollEl) return;

    const sections = [
      "basic-info",
      "pricing-inventory",
      "dimensions-weight",
      "images",
      "variations",
      "price-ranges",
      "product-details",
      "seo-keywords",
      "draft-info",
    ];

    const handleScroll = () => {
      const scrollPosition = scrollEl.scrollTop + 200;
      for (let i = sections.length - 1; i >= 0; i--) {
        const section = scrollEl.querySelector(`#${CSS.escape(sections[i])}`);
        if (section && section.offsetTop <= scrollPosition) {
          setActiveSection(sections[i]);
          break;
        }
      }
    };

    scrollEl.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => scrollEl.removeEventListener("scroll", handleScroll);
  }, []);

  // AI provider/config state (provider persisted; keys read from .env)
  const [aiProvider, setAiProvider] = useState(() => sessionStorage.getItem('ai_provider') || 'local');
  const [aiCalls, setAiCalls] = useState(Number(sessionStorage.getItem('ai_calls') || '0'));
  const [aiLoadingTitle, setAiLoadingTitle] = useState(false);
  const [aiLoadingDesc, setAiLoadingDesc] = useState(false);
  const [aiLoadingAll, setAiLoadingAll] = useState(false);
  const [aiPrompts, setAiPrompts] = useState({}); // Prompts from database keyed by action
  const [aiProviders, setAiProviders] = useState({ openai: false, deepseek: false, gemini: false, bedrock: false }); // Available providers from backend
  const [aiModels, setAiModels] = useState([]); // Available AI models from database

  const [isTranslatingIndex, setIsTranslatingIndex] = useState(null);

  const [variantUpdating, setVariantUpdating] = useState(false);

  const [priceLogs, setPriceLogs] = useState(null);      // null = loading state
  const [priceLogLoading, setPriceLogLoading] = useState(false);

  // ===== Remark Modal (Variant / Product) =====
  const [remarkModalOpen, setRemarkModalOpen] = useState(false);
  const [remarkText, setRemarkText] = useState("");
  const remarkContextRef = useRef({ type: null, index: null }); // { type: "VARIANT"|"PRODUCT", index?: number }

  // original snapshot for change-check
  const originalDataRef = useRef(null);


  // Track base finalPrice for each variant (latest saved finalPrice)
  // This is used for percentage calculations
  const baseFinalPriceRef = useRef({}); // { variantKey: finalPrice }

  // Track base wholesaleFinalPrice for each variant (latest saved wholesaleFinalPrice)
  // This is used for wholesale percentage calculations to prevent cumulative calculations
  const baseWholesalePriceRef = useRef({}); // { variantKey: wholesaleFinalPrice }

  // Variant price modal state
  const [variantPriceModalOpen, setVariantPriceModalOpen] = useState(false);
  const [activeVariant, setActiveVariant] = useState(null);
  const [activeVariantIndex, setActiveVariantIndex] = useState(null);

  // Bulk % markup & Target  price  (applies to ALL variants)
  const [bulkPercentage, setBulkPercentage] = useState("");
  const [bulkTargetPrice, setBulkTargetPrice] = useState("");
  const [bulkMarkupMode, setBulkMarkupMode] = useState("percentage"); // default selected
  const [variantBulkMenuOpen, setVariantBulkMenuOpen] = useState(false);
  const variantBulkMenuRef = useRef(null);

  // Pricing mode: "dropshipping" or "wholesale"
  const [pricingMode, setPricingMode] = useState("dropshipping");

  // Store original variant values to reset unsaved changes on tab switch
  const originalVariantsRef = useRef(null);

  // Handle pricing mode change with reset logic for unsaved changes
  const handlePricingModeChange = (newMode) => {
    if (newMode === pricingMode) return; // No change needed

    //  Commented reset logic (we don't want reset anymore)
    // Check if there are unsaved changes in variant fields (length, width, height, weight)
    // if (originalVariantsRef.current && Array.isArray(draft.variations)) {
    //   const hasUnsavedChanges = draft.variations.some((variant, index) => {
    //     const originalVariant = originalVariantsRef.current.find(ov => 
    //       (ov.skuId && variant.skuId && String(ov.skuId) === String(variant.skuId)) ||
    //       (ov.specId && variant.specId && String(ov.specId) === String(variant.specId))
    //     ) || originalVariantsRef.current[index];

    //     if (!originalVariant) return false;

    //     // Check dimension fields for unsaved changes
    //     return (
    //       (variant.length ?? null) !== (originalVariant.length ?? null) ||
    //       (variant.width ?? null) !== (originalVariant.width ?? null) ||
    //       (variant.height ?? null) !== (originalVariant.height ?? null) ||
    //       (variant.weight ?? null) !== (originalVariant.weight ?? null)
    //     );
    //   });

    //   // If there are unsaved changes, reset to original values
    //   if (hasUnsavedChanges) {
    //     const resetVariations = draft.variations.map((variant, index) => {
    //       const originalVariant = originalVariantsRef.current.find(ov => 
    //         (ov.skuId && variant.skuId && String(ov.skuId) === String(variant.skuId)) ||
    //         (ov.specId && variant.specId && String(ov.specId) === String(variant.specId))
    //       ) || originalVariantsRef.current[index];

    //       if (!originalVariant) return variant;

    //       // Reset dimension fields to original values
    //       return {
    //         ...variant,
    //         length: originalVariant.length ?? variant.length,
    //         width: originalVariant.width ?? variant.width,
    //         height: originalVariant.height ?? variant.height,
    //         weight: originalVariant.weight ?? variant.weight,
    //       };
    //     });

    //     setDraft(prev => ({
    //       ...prev,
    //       variations: resetVariations
    //     }));
    //   }
    // }

    // Change pricing mode
    setPricingMode(newMode);
  };
  const isBulkApplyDisabled =
    bulkMarkupMode === "percentage"
      ? bulkPercentage === "" || Number(bulkPercentage) <= 0
      : bulkTargetPrice === "" || Number(bulkTargetPrice) <= 0;


  // Bulk menu: current selection status (for showing tick icon)
  const totalVariants = Array.isArray(draft?.variations) ? draft.variations.length : 0;

  const selectedCount = Array.isArray(draft?.variations)
    ? draft.variations.reduce((acc, v, i) => {
      const key = v?.sku || v?.skuId || v?.specId || `${v?.color || ""}-${v?.size || ""}` || String(i);
      return acc + (key && variantSelection?.[key] ? 1 : 0);
    }, 0)
    : 0;

  const allVariantsChecked = totalVariants > 0 && selectedCount === totalVariants;
  const allVariantsUnchecked = totalVariants > 0 && selectedCount === 0;

  useEffect(() => {
    const onDocClick = (e) => {
      if (!variantBulkMenuRef.current) return;
      if (!variantBulkMenuRef.current.contains(e.target)) {
        setVariantBulkMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  useEffect(() => {
    sessionStorage.setItem('ai_provider', aiProvider);
  }, [aiProvider]);
  useEffect(() => {
    sessionStorage.setItem('ai_calls', String(aiCalls));
  }, [aiCalls]);

  // Safe env getter (Vite/CRA/node)
  const getEnv = (k) => {
    try {
      // Vite
      // eslint-disable-next-line no-undef
      if (typeof import.meta !== 'undefined' && import.meta && import.meta.env && (k in import.meta.env)) {
        // eslint-disable-next-line no-undef
        return String(import.meta.env[k] || '');
      }
    } catch { }
    try {
      // CRA / Node
      if (typeof process !== 'undefined' && process.env && (k in process.env)) {
        return String(process.env[k] || '');
      }
    } catch { }
    return '';
  };

  const openVariantPriceModal = (variation, index) => {
    setActiveVariant(variation);
    setActiveVariantIndex(index);
    setVariantPriceModalOpen(true);
  };

  const closeVariantPriceModal = () => {
    setVariantPriceModalOpen(false);
    setActiveVariant(null);
    setActiveVariantIndex(null);
  };


  const applyVariantDimensions = (dimensions) => {
    if (activeVariantIndex == null) return;

    // Use functional update to get the latest state (which includes price updates)
    setDraft((prev) => {
      const updatedVariations = [...prev.variations];
      const currentVar = updatedVariations[activeVariantIndex];

      // Update all dimensions at once, preserving any existing updates
      updatedVariations[activeVariantIndex] = {
        ...currentVar,
        ...(dimensions.weight != null && { weight: dimensions.weight }),
        ...(dimensions.length != null && { length: dimensions.length }),
        ...(dimensions.height != null && { height: dimensions.height }),
        ...(dimensions.width != null && { width: dimensions.width }),
      };

      return { ...prev, variations: updatedVariations };
    });
    setHasUnsavedChanges(true);
  };

  const applyVariantPrice = (finalPrice, wholesaleFinalPrice = null) => {
    if (activeVariantIndex == null) return;

    // Use functional update to get the latest state (which includes dimension updates)
    setDraft((prev) => {
      const updatedVariations = [...prev.variations];

      const currentVar = updatedVariations[activeVariantIndex];
      const variantKey = getVarKey(currentVar, activeVariantIndex);

      // Update both DS and WS prices if provided
      if (finalPrice != null) {
        // For dropshipping: update finalPrice
        // Get base finalPrice (latest saved) for percentage calculation
        let baseFinalPrice = null;

        // Get latest saved finalPrice from baseFinalPriceRef
        if (baseFinalPriceRef.current[variantKey] != null) {
          baseFinalPrice = baseFinalPriceRef.current[variantKey];
        }

        // Fallback to originalDataRef if baseFinalPriceRef doesn't have it
        if (baseFinalPrice == null && originalDataRef.current && Array.isArray(originalDataRef.current.variations)) {
          const origVar = originalDataRef.current.variations[activeVariantIndex];
          // Try to match by skuId or specId to get the correct original variant
          const matchedOrig = originalDataRef.current.variations.find(ov =>
            (ov.skuId && currentVar.skuId && String(ov.skuId) === String(currentVar.skuId)) ||
            (ov.specId && currentVar.specId && String(ov.specId) === String(currentVar.specId))
          ) || origVar;
          if (matchedOrig && matchedOrig.finalPrice != null) {
            baseFinalPrice = matchedOrig.finalPrice;
          }
        }

        // If still not found, use current finalPrice
        if (baseFinalPrice == null && currentVar.finalPrice != null) {
          baseFinalPrice = currentVar.finalPrice;
        }

        // If baseFinalPrice is still null, use the new finalPrice as base (first time setting)
        if (baseFinalPrice == null) {
          baseFinalPrice = finalPrice;
        }

        // Set targetPrice to the calculated price, which will automatically set finalPrice = targetPrice
        const v = { ...updatedVariations[activeVariantIndex], targetPrice: finalPrice };

        const syncedVariant = syncVariantPricing(v, "targetPrice", baseFinalPrice, null, "dropshipping");
        updatedVariations[activeVariantIndex] = syncedVariant;

        // Update baseFinalPriceRef for next time
        baseFinalPriceRef.current = {
          ...baseFinalPriceRef.current,
          [variantKey]: finalPrice,
        };
      }

      if (wholesaleFinalPrice != null) {
        // For wholesale: update wholesaleFinalPrice
        // Get base wholesaleFinalPrice (latest saved) for percentage calculation
        let baseWholesalePrice = null;

        // Get latest saved wholesaleFinalPrice from baseWholesalePriceRef
        if (baseWholesalePriceRef.current[variantKey] != null) {
          baseWholesalePrice = baseWholesalePriceRef.current[variantKey];
        }

        // Fallback to originalDataRef if baseWholesalePriceRef doesn't have it
        if (baseWholesalePrice == null && originalDataRef.current && Array.isArray(originalDataRef.current.variations)) {
          const origVar = originalDataRef.current.variations[activeVariantIndex];
          // Try to match by skuId or specId to get the correct original variant
          const matchedOrig = originalDataRef.current.variations.find(ov =>
            (ov.skuId && currentVar.skuId && String(ov.skuId) === String(currentVar.skuId)) ||
            (ov.specId && currentVar.specId && String(ov.specId) === String(currentVar.specId))
          ) || origVar;
          if (matchedOrig && matchedOrig.wholesaleFinalPrice != null) {
            baseWholesalePrice = matchedOrig.wholesaleFinalPrice;
          }
        }

        // If still not found, use current wholesaleFinalPrice
        if (baseWholesalePrice == null && currentVar.wholesaleFinalPrice != null) {
          baseWholesalePrice = currentVar.wholesaleFinalPrice;
        }

        // Set wholesaleTargetPrice to the calculated price, which will automatically set wholesaleFinalPrice = wholesaleTargetPrice
        const v = { ...updatedVariations[activeVariantIndex], wholesaleTargetPrice: wholesaleFinalPrice };

        updatedVariations[activeVariantIndex] = syncVariantPricing(
          v,
          "wholesaleTargetPrice",
          baseWholesalePrice, // baseFinalPrice parameter - pass baseWholesalePrice for wholesale
          null, // originalFinalPrice fallback
          "wholesale" // pricingMode
        );

        // Update baseWholesalePriceRef for next time
        baseWholesalePriceRef.current = {
          ...baseWholesalePriceRef.current,
          [variantKey]: wholesaleFinalPrice,
        };
      }

      // Recalculate SP from minimum variant finalPrice (INR)
      const updatedDraft = { ...prev, variations: updatedVariations };
      const minPrice = getMinVariantPrice(updatedVariations);
      if (minPrice != null && minPrice > 0) {
        updatedDraft.sp = minPrice;
        updatedDraft.mrp = Number((minPrice * 2.5).toFixed(2));
      }

      return updatedDraft;
    });
    setHasUnsavedChanges(true);
  };


  /** Apply calculated prices and dimensions to multiple variants at once */
  const applyMultipleVariants = (results) => {
    if (!results || !results.length) return;

    setDraft((prev) => {
      const updatedVariations = [...prev.variations];

      results.forEach(({ index, finalPrice, wholesaleFinalPrice, dimensions: dims }) => {
        if (index < 0 || index >= updatedVariations.length) return;
        let v = { ...updatedVariations[index] };
        const variantKey = getVarKey(v, index);

        // Apply dimensions
        if (dims) {
          if (dims.weight != null) v.weight = dims.weight;
          if (dims.length != null) v.length = dims.length;
          if (dims.height != null) v.height = dims.height;
          if (dims.width != null) v.width = dims.width;
        }

        // Apply DS price
        if (finalPrice != null) {
          let baseFinalPrice = baseFinalPriceRef.current[variantKey] ?? null;
          let prevPercentage = v.percentage;
          if (baseFinalPrice == null && originalDataRef.current?.variations) {
            const origVar = originalDataRef.current.variations.find(ov =>
              (ov.skuId && v.skuId && String(ov.skuId) === String(v.skuId)) ||
              (ov.specId && v.specId && String(ov.specId) === String(v.specId))
            );
            if (origVar?.finalPrice != null) baseFinalPrice = origVar.finalPrice;
            if (prevPercentage == null && origVar?.percentage != null) prevPercentage = origVar.percentage;
          }
          if (baseFinalPrice == null) baseFinalPrice = v.finalPrice ?? finalPrice;

          v = { ...v, targetPrice: finalPrice };
          v = syncVariantPricing(v, "targetPrice", baseFinalPrice, null, "dropshipping");
          if (finalPrice === baseFinalPrice && prevPercentage != null) {
            v.percentage = prevPercentage;
          }
          baseFinalPriceRef.current = { ...baseFinalPriceRef.current, [variantKey]: finalPrice };
        }

        // Apply WS price
        if (wholesaleFinalPrice != null) {
          let baseWholesalePrice = baseWholesalePriceRef.current[variantKey] ?? null;
          let prevWholesalePercentage = v.wholesalePercentage;
          if (baseWholesalePrice == null && originalDataRef.current?.variations) {
            const origVar = originalDataRef.current.variations.find(ov =>
              (ov.skuId && v.skuId && String(ov.skuId) === String(v.skuId)) ||
              (ov.specId && v.specId && String(ov.specId) === String(v.specId))
            );
            if (origVar?.wholesaleFinalPrice != null) baseWholesalePrice = origVar.wholesaleFinalPrice;
            if (prevWholesalePercentage == null && origVar?.wholesalePercentage != null) prevWholesalePercentage = origVar.wholesalePercentage;
          }
          if (baseWholesalePrice == null) baseWholesalePrice = v.wholesaleFinalPrice ?? wholesaleFinalPrice;

          v = { ...v, wholesaleTargetPrice: wholesaleFinalPrice };
          v = syncVariantPricing(v, "wholesaleTargetPrice", baseWholesalePrice, null, "wholesale");
          if (wholesaleFinalPrice === baseWholesalePrice && prevWholesalePercentage != null) {
            v.wholesalePercentage = prevWholesalePercentage;
          }
          baseWholesalePriceRef.current = { ...baseWholesalePriceRef.current, [variantKey]: wholesaleFinalPrice };
        }

        updatedVariations[index] = v;
      });

      // Recalculate SP from minimum variant finalPrice (INR)
      const updatedDraft = { ...prev, variations: updatedVariations };
      const minPrice = getMinVariantPrice(updatedVariations);
      if (minPrice != null && minPrice > 0) {
        updatedDraft.sp = minPrice;
        updatedDraft.mrp = Number((minPrice * 2.5).toFixed(2));
      }

      return updatedDraft;
    });
    setHasUnsavedChanges(true);
  };



  const handleUpdateVariant = (index) => {
    //  Always open modal on variant update click
    openRemarkModal("VARIANT", index);
  };

  const submitVariantUpdateWithRemark = (index, remark) => {
    if (variantUpdating || loading) return; //prevent double call
    setVariantUpdating(true);
    loader.showLoading(true);
    const variant = draft.variations[index];

    // Check if SP needs to be updated (compare original SP from DB with minimum variant price)
    // Use original SP from originalDataRef, not current state SP (which might already be updated)
    const originalSp = originalDataRef.current?.sp ?? draft.sp;
    const minPrice = getMinVariantPrice(draft.variations);
    const spNeedsUpdate = minPrice != null && minPrice > 0 && originalSp !== minPrice;

    // If SP needs update, call update draft instead of just variant
    if (spNeedsUpdate) {
      // Reset variant updating state first (doUpdateDraft will handle its own loading state)
      setVariantUpdating(false);
      loader.showLoading(false);

      // Calculate new SP and MRP
      const newSp = minPrice;
      const newMrp = Number((minPrice * 2.5).toFixed(2));

      // Update SP and MRP in draft with the new values
      const updatedDraft = {
        ...draft,
        sp: newSp,
        mrp: newMrp
      };
      setDraft(updatedDraft);

      // Call update draft with updated SP/MRP directly (to ensure correct values are sent)
      // This will update SP and all variants, and SP change will be logged
      doUpdateDraft(remark, newSp, newMrp);
      return;
    }

    // Create a payload with the updated variant data
    const payload = {
      skuId: variant.skuId,
      specId: variant.specId,
      update: {
        finalPrice: variant.finalPrice,
        finalPriceMRP: variant.finalPriceMRP,
        actualPrice: variant.actualPrice,
        percentage: variant.percentage,
        targetPrice: variant.targetPrice,
        wholesaleFinalPrice: variant.wholesaleFinalPrice,
        wholesalePercentage: variant.wholesalePercentage,
        wholesaleTargetPrice: variant.wholesaleTargetPrice,
        amountOnSale: variant.amountOnSale,
        length: variant.length,
        width: variant.width,
        height: variant.height,
        weight: variant.weight,
      },
      remark, // mandatory remark send
      pricingMode, // Pass current pricing mode (dropshipping or wholesale) for price log
    };

    // Call the API to update the variant
    productDraftApiService
      .updateProductDraftVariant(id, payload)
      .then((res) => {
        if (res.status.code === 200) {
          message.showToast("Variant updated successfully", ToastTypes.SUCCESS);
          // Update base price refs with the new saved price (based on current pricing mode)
          const variantKey = getVarKey(variant, index);
          if (variantKey) {
            if (pricingMode === "wholesale") {
              // Update baseWholesalePriceRef for wholesale mode
              const baseWholesalePrice = variant.wholesaleFinalPrice != null ? variant.wholesaleFinalPrice : null;
              if (baseWholesalePrice != null) {
                baseWholesalePriceRef.current = {
                  ...baseWholesalePriceRef.current,
                  [variantKey]: baseWholesalePrice
                };
              }
            } else {
              // Update baseFinalPriceRef for dropshipping mode
              const basePrice = variant.finalPrice != null ? variant.finalPrice : null;
              if (basePrice != null) {
                baseFinalPriceRef.current = {
                  ...baseFinalPriceRef.current,
                  [variantKey]: basePrice
                };
              }
            }
          }
          fetchDraftData(); // refresh product + pricelog (will update originalVariantsRef)
        } else {
          message.showToast(res.error.message, ToastTypes.ERROR);
        }
      })
      .catch((err) => {
        message.showToast("Failed to update variant", ToastTypes.ERROR);
      })
      .finally(() => {
        setVariantUpdating(false);
        loader.showLoading(false);
      });
  };


  const fetchPriceLogs = (pid, offId) => {
    if (!pid) return;
    setPriceLogLoading(true);

    const qs = new URLSearchParams();
    qs.set("page", "1");
    qs.set("limit", "100");
    // qs.set("status", "6");
    if (offId) qs.set("offerId", offId);

    productListApiService
      .getProductPriceLogs(pid, `?${qs.toString()}`)
      .then((res) => {
        if (res?.status?.code === 200) {
          setPriceLogs(res?.data?.logs || []);
        } else {
          setPriceLogs([]);
        }
        setPriceLogLoading(false);
      })
      .catch(() => {
        setPriceLogs([]);
        setPriceLogLoading(false);
      });
  };


  const getApiKeyForProvider = (provider) => {
    const p = String(provider || '').toLowerCase();
    if (p === 'openai') {
      return (
        getEnv('REACT_APP_OPENAI_API_KEY') ||
        getEnv('VITE_OPENAI_API_KEY') ||
        getEnv('OPENAI_API_KEY')
      );
    }
    if (p === 'deepseek') {
      return (
        getEnv('REACT_APP_DEEPSEEK_API_KEY') ||
        getEnv('VITE_DEEPSEEK_API_KEY') ||
        getEnv('DEEPSEEK_API_KEY')
      );
    }
    return '';
  };

  const getExpectedEnvVarNames = (provider) => {
    const p = String(provider || '').toLowerCase();
    if (p === 'openai') return ['REACT_APP_OPENAI_API_KEY', 'VITE_OPENAI_API_KEY', 'OPENAI_API_KEY'];
    if (p === 'deepseek') return ['REACT_APP_DEEPSEEK_API_KEY', 'VITE_DEEPSEEK_API_KEY', 'DEEPSEEK_API_KEY'];
    return [];
  };

  // Helper to call OpenAI or DeepSeek chat completions (via backend)
  const callAI = async ({ prompt, maxTokens = 2048, type = 'title' }) => {
    const provider = String(aiProvider || 'local').toLowerCase();
    if (provider === 'local') {
      throw new Error('Local provider selected');
    }
    // Use backend proxy so keys can live in backend .env
    try {
      const { AdminAIService } = await import('../../../../service/admin/aiService');
      const resp = await AdminAIService.generate({ provider, type, prompt, maxTokens });
      if (resp?.status?.code === 200 && resp?.data?.text) {
        setAiCalls((n) => n + 1);
        return String(resp.data.text || '').trim();
      }
      const message = resp?.error?.message || 'AI generate failed';
      throw new Error(message);
    } catch (e) {
      throw e;
    }
  };

  // Marketing-style title generator using requested patterns
  const generateAiTitle = (data) => {
    const rawTitle = String(data.title || "");
    const keywordsPool = [
      String(data.style_name || ""),
      String(data.item_type_name || ""),
      String(data.feed_product_type || ""),
      ...(Array.isArray(data.search_terms) ? data.search_terms : []),
      String(data.generic_keywords || ""),
    ]
      .join(" ")
      .toLowerCase();

    const themes = [
      { key: "animal", lead: "Animal" },
      { key: "dinosaur", lead: "Dinosaur" },
      { key: "cat", lead: "Cat" },
      { key: "dog", lead: "Dog" },
      { key: "bear", lead: "Bear" },
      { key: "unicorn", lead: "Unicorn" },
      { key: "emoji", lead: "Emoji" },
    ];
    const matchedTheme = themes.find(t => rawTitle.toLowerCase().includes(t.key) || keywordsPool.includes(t.key));
    const themeLead = matchedTheme ? matchedTheme.lead : (String(data.style_name || data.item_type_name || "").trim() || "Creative");

    const leadLong = `${themeLead} Bubble Pop Fidget Toy`;
    const leadShort = `${themeLead} Shape Bubble Toy`;

    const templates = [
      `${leadLong} – Stress Relief Squeeze Toy for Kids & Adults, Creative Sensory Squishy Toy for Anxiety & Relaxation`,
      `Trendy ${leadShort} – Viral Stress Relief Fidget, Squeeze Pop Sensory Toy for Focus, Relaxation & Kids Gifts`,
      `Cute ${leadShort} – Stress Relief Squeeze Toy, Creative Fidget Sensory Toy for Kids, Teens & Adults`,
      `Viral ${leadShort} – Stress Relief Squeeze Pop Toy, Creative Sensory Squishy Toy for Anxiety, School & Office`,
      `Creative ${leadShort} – Fun Stress Relief Fidget, Squeeze Sensory Toy for Kids, Adults, Gifts & Party Favors`,
    ];

    const seed = String(data.sku || "").split("").reduce((a, c) => a + c.charCodeAt(0), 0);
    let title = templates[seed % templates.length];

    const maxLen = 160;
    if (title.length > maxLen) {
      const trimmed = title.slice(0, maxLen);
      title = trimmed.replace(/[ ,;:&-]+$/, "");
    }
    return title;
  };

  const handleGenerateTitle = async () => {
    try {
      setAiLoadingTitle(true);
      const provider = String(aiProvider || 'local').toLowerCase();
      if (provider === 'openai' || provider === 'deepseek' || provider === 'bedrock') {
        // Get prompt from database or use fallback
        const dbPrompt = aiPrompts['generate-title'];
        let prompt;
        if (dbPrompt?.userPromptTemplate) {
          // Replace template variables
          prompt = dbPrompt.userPromptTemplate
            .replace(/\{\{draftData\}\}/g, JSON.stringify(draft).slice(0, 2000))
            .replace(/\{\{title\}\}/g, draft.title || '')
            .replace(/\{\{category\}\}/g, selectedCategoryFullPath.map(c => c.c_name).join(' > ') || '')
            .replace(/\{\{brand\}\}/g, draft.brand || '');
        } else {
          // Fallback to hardcoded prompt
          prompt = `You are a product copywriter. Generate ONE single-sentence product title (return only the title, one line, no quotes or extra text) in the style of these examples:\n\n` +
            `1) Animal Bubble Pop Fidget Toy – Stress Relief Squeeze Toy for Kids & Adults, Creative Sensory Squishy Toy for Anxiety & Relaxation\n` +
            `2) Trendy Animal Shape Bubble Toy – Viral Stress Relief Fidget, Squeeze Pop Sensory Toy for Focus, Relaxation & Kids Gifts\n` +
            `3) Cute Bubble Pop Animal Toy – Stress Relief Squeeze Toy, Creative Fidget Sensory Toy for Kids, Teens & Adults\n` +
            `4) Viral Animal Bubble Fidget Toy – Stress Relief Squeeze Pop Toy, Creative Sensory Squishy Toy for Anxiety, School & Office\n` +
            `5) Creative Animal Bubble Pop Toy – Fun Stress Relief Fidget, Squeeze Sensory Toy for Kids, Adults, Gifts & Party Favors\n\n` +
            `Draft data: ${JSON.stringify(draft).slice(0, 2000)}\n\nReturn ONLY the title text.`;
        }
        const maxTokens = dbPrompt?.maxTokens || 200;
        const aiTitle = await callAI({ prompt, maxTokens, type: 'title' });
        if (aiTitle) {
          setDraft((prev) => ({ ...prev, title: aiTitle }));
          setHasUnsavedChanges(true);
          message.showToast('AI title generated', ToastTypes.SUCCESS);
        }
        return;
      }
      // Local provider only
      const newTitle = generateAiTitle(draft);
      setDraft((prev) => ({ ...prev, title: newTitle }));
      setHasUnsavedChanges(true);
      message.showToast("Title generated", ToastTypes.SUCCESS);
    } catch (e) {
      message.showToast(String(e?.message || 'AI title failed'), ToastTypes.ERROR);
    } finally {
      setAiLoadingTitle(false);
    }
  };

  // Generate a 1500-word description based on the current draft (AI preferred)
  const generateAiDescription150 = (data) => {
    const brand = (data.brand || "Generic").trim();
    const category = selectedCategoryFullPath.length
      ? selectedCategoryFullPath[selectedCategoryFullPath.length - 1]?.c_name
      : (data.feed_product_type || data.item_type_name || "Product");
    const keyPhrases = [
      data.style_name,
      data.material_type,
      data.pattern_type,
      data.fit_type,
      data.lifestyle,
      data.color_map,
      data.size_map,
    ].filter(Boolean);
    const benefits = [
      "crafted for all-day comfort",
      "made with attention to detail",
      "easy to style for multiple occasions",
      "built for everyday durability",
      "lightweight yet dependable",
      "designed to elevate your look",
      "finishes that feel premium",
      "a thoughtful balance of function and form",
      "simple care and lasting color",
      "ideal for gifting or personal use",
    ];
    const sentences = [];
    sentences.push(
      `${brand} presents a ${category.toLowerCase()} designed to bring confidence and ease to your daily routine.`
    );
    if (keyPhrases.length) {
      sentences.push(
        `Inspired by ${keyPhrases.filter(Boolean).slice(0, 3).join(
          ", "
        )}, its refined silhouette pairs effortlessly with your wardrobe.`
      );
    }
    sentences.push(
      `Soft textures, clean lines and practical details come together to deliver a polished, versatile essential.`
    );
    sentences.push(
      `Wear it for errands, casual meetups or travel—the balanced proportions ensure a flattering, relaxed fit.`
    );
    sentences.push(
      `Every element is thoughtfully considered, from the feel against the skin to the way it holds shape throughout the day.`
    );
    sentences.push(
      `It layers well across seasons and complements denim, chinos or tailored pieces without effort.`
    );
    sentences.push(
      `Whether you prefer minimal styling or bold accessories, this piece adapts and always looks intentional.`
    );
    sentences.push(
      `Quality construction supports daily wear while keeping weight low and comfort high.`
    );
    sentences.push(
      `Care is simple—wash, dry and go—so it's ready whenever inspiration strikes.`
    );
    sentences.push(
      `Choose it when you want something dependable, modern and quietly elevated.`
    );
    const shuffled = benefits.sort(() => Math.random() - 0.5).slice(0, 3);
    shuffled.forEach((b) => sentences.push(`It is ${b}.`));

    let text = sentences.join(" ");
    let words = text
      .replace(/\s+/g, " ")
      .trim()
      .split(" ")
      .filter(Boolean);
    const filler = [
      "simply",
      "beautifully",
      "effortlessly",
      "gently",
      "reliably",
      "thoughtfully",
      "precisely",
      "seamlessly",
    ];
    while (words.length < 500) {
      words.push(filler[Math.floor(Math.random() * filler.length)]);
    }
    if (words.length > 500) {
      words = words.slice(0, 500);
    }
    const finalText = words.join(" ");
    return `<p>${finalText}</p>`;
  };

  const handleGenerateDescription = async () => {
    try {
      setAiLoadingDesc(true);
      const existingHtml = (() => { try { return editorRef.current?.getData?.() || draft.description || ''; } catch { return draft.description || ''; } })();
      const provider = String(aiProvider || 'local').toLowerCase();

      if (provider === 'openai' || provider === 'deepseek' || provider === 'bedrock') {
        // Get prompt from database or use fallback
        const dbPrompt = aiPrompts['generate-description'];
        let prompt;
        if (dbPrompt?.userPromptTemplate) {
          prompt = dbPrompt.userPromptTemplate
            .replace(/\{\{draftData\}\}/g, JSON.stringify(draft).slice(0, 2000))
            .replace(/\{\{title\}\}/g, draft.title || '')
            .replace(/\{\{description\}\}/g, draft.description || '')
            .replace(/\{\{category\}\}/g, selectedCategoryFullPath.map(c => c.c_name).join(' > ') || '')
            .replace(/\{\{brand\}\}/g, draft.brand || '')
            .replace(/\{\{features\}\}/g, (draft.bullet_points || []).join(', '));
        } else {
          prompt = `You are a product copywriter. Write a 500-word, conversion-focused product description in HTML (basic <p>, <ul>, <li>, <strong> only). Do NOT include images or remove any existing images in the editor. The generated HTML will be inserted ABOVE existing images.\n\nInclude: benefits, use-cases, sensory words, gift ideas, and a clear CTA. Keep it skimmable and readable.\n\nDraft data: ${JSON.stringify(draft).slice(0, 2000)}\n\nReturn ONLY the HTML.`;
        }
        const maxTokens = dbPrompt?.maxTokens || 900;
        const aiHtml = await callAI({ prompt, maxTokens, type: 'description' });
        const combined = composeDescriptionWithImages(aiHtml, existingHtml);
        setDraft((prev) => ({ ...prev, description: combined }));
        try { editorRef.current?.setData?.(combined); ensureEditorImagesSafe(); } catch { }
        setHasUnsavedChanges(true);
        message.showToast('AI description generated', ToastTypes.SUCCESS);
        return;
      }

      // Local provider only
      const html = generateAiDescription150(draft);
      const combined = composeDescriptionWithImages(html, existingHtml);
      setDraft((prev) => ({ ...prev, description: combined }));
      try { editorRef.current?.setData?.(combined); ensureEditorImagesSafe(); } catch { }
      setHasUnsavedChanges(true);
      message.showToast("Generated 500-word description", ToastTypes.SUCCESS);
    } catch (e) {
      message.showToast(String(e?.message || 'AI description failed'), ToastTypes.ERROR);
    } finally {
      setAiLoadingDesc(false);
    }
  };

  const expandAll = () => {
    const next = {};
    sectionIds.forEach((id) => (next[id] = false));
    setCollapsed(next);
  };
  const collapseAll = () => {
    const next = {};
    sectionIds.forEach((id) => (next[id] = true));
    setCollapsed(next);
  };

  // Variations complete check (same intent as Product List)
  const isVariationsComplete = Array.isArray(draft.variations)
    && draft.variations.length > 0
    && draft.variations.every((v) => {
      // consider only selected variants
      const key = v?.sku || v?.skuId || v?.specId || `${v?.color || ""}-${v?.size || ""}`;
      if (!key || !variantSelection[key]) return true;

      // required fields for a variant to be considered complete
      return (
        Number(v.finalPrice) > 0 &&
        Number(v.finalPriceMRP) > 0 &&
        (v.ap ?? v.actualPrice) !== undefined
      );
    });

  // Section completion checks for UX
  const isBasicComplete = !!(draft.title || draft.sku || draft.categoryId);
  const isPricingComplete = !!((draft.sp || draft.vendor_price) || draft.quantity !== "");
  // const isDimensionsComplete = !!(draft.length && draft.width && draft.height && (draft.weight !== "" && draft.weight !== 0));
  const isImagesComplete = Array.isArray(draft.imgUrl) && draft.imgUrl.length > 0;
  const isDetailsComplete = !!(draft.feed_product_type || draft.style_name || draft.color_map || draft.size_map);
  const isSeoComplete = !!(draft.generic_keywords || (draft.search_terms && draft.search_terms.length > 0));
  const isDraftInfoComplete = !!(draft.location);
  // const requiredSectionStatus = [isBasicComplete, isPricingComplete, isDimensionsComplete, isImagesComplete];
  const requiredSectionStatus = [isBasicComplete, isPricingComplete, isImagesComplete];
  const optionalSectionStatus = [isDetailsComplete, isSeoComplete, isDraftInfoComplete];
  const completedRequired = requiredSectionStatus.filter(Boolean).length;
  const totalRequired = requiredSectionStatus.length;
  const completionPercent = Math.round((completedRequired / totalRequired) * 100);

  const ensureEditorImagesSafe = () => {
    try {
      const root = editorRef.current?.ui?.view?.editable?.element;
      if (!root) return;
      const imgs = root.querySelectorAll('img');
      imgs.forEach((img) => {
        try {
          const src = img.getAttribute('src') || '';
          if (/^http:\/\//.test(src)) img.setAttribute('src', src.replace('http://', 'https://'));
          if (/^\/\//.test(src)) img.setAttribute('src', 'https:' + src);
          if (!img.getAttribute('referrerpolicy')) img.setAttribute('referrerpolicy', 'no-referrer');
          if (!img.getAttribute('crossorigin')) img.setAttribute('crossorigin', 'anonymous');
        } catch { }
      });
    } catch { }
  };

  // Compose new description + existing images only (place AI text above images)
  const composeDescriptionWithImages = (aiHtml, existingHtml) => {
    try {
      const imgs = String(existingHtml || '').match(/<img[^>]*>/gi) || [];
      const imagesOnly = imgs.join('<br/>');
      return aiHtml + (imagesOnly ? ('<br/>' + imagesOnly) : '');
    } catch {
      return String(aiHtml || '');
    }
  };

  // Attach floating AI action for images inside CKEditor
  const setupCkImageAi = (editorInstance) => {
    try {
      const editable = editorInstance?.ui?.view?.editable?.element;
      if (!editable) return;

      // Create small floating icon button once
      let currentImgEl = null;
      const aiBtn = document.createElement('button');
      aiBtn.type = 'button';
      aiBtn.textContent = 'AI';
      aiBtn.style.position = 'absolute';
      aiBtn.style.display = 'none';
      // Increased size for better tap target
      aiBtn.style.width = '30px';
      aiBtn.style.height = '30px';
      aiBtn.style.lineHeight = '28px';
      aiBtn.style.textAlign = 'center';
      aiBtn.style.background = '#111827';
      aiBtn.style.color = '#fff';
      aiBtn.style.border = '1px solid #e5e7eb';
      aiBtn.style.borderRadius = '50%';
      aiBtn.style.boxShadow = '0 4px 10px rgba(0,0,0,0.15)';
      aiBtn.style.zIndex = '9999';
      aiBtn.style.cursor = 'pointer';
      aiBtn.title = 'Translate image text to English';

      aiBtn.onclick = async () => {
        if (!currentImgEl) return;
        const imageUrl = currentImgEl.getAttribute('src') || '';
        try {
          message.showToast('Translating image text…', ToastTypes.INFO);

          const baseUrl = process.env.REACT_APP_APIUrl1688;
          const token = await fetchToken();
          const transResp = await fetch(baseUrl + '/product-image-trans', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-API-Token': token },
            body: JSON.stringify({
              imageUrl,
              originalLanguage: 'zh',
              targetLanguage: 'en'
            })
          });

          const transJson = await transResp.json();
          // console.log('CKEditor translation response:', transJson);

          const translatedUrl = transJson?.result?.result?.translatedImageUrl;

          if (!translatedUrl) {
            console.error('No translated URL found in CKEditor response:', transJson);
            message.showToast('Failed to translate image - no URL returned', ToastTypes.ERROR);
            return;
          }

          // console.log('CKEditor updating image from:', imageUrl, 'to:', translatedUrl);

          // Use actual translatedImageUrl directly (no proxy)
          try {
            const oldHtml = editorInstance.getData();
            const newHtml = (oldHtml || '').split(imageUrl).join(translatedUrl);
            editorInstance.setData(newHtml);
            setDraft(prev => ({ ...prev, description: newHtml }));
          } catch {
            // Fallback: direct DOM update then read back
            currentImgEl.setAttribute('src', translatedUrl);
            const newData = editorInstance.getData();
            setDraft(prev => ({ ...prev, description: newData }));
          }

          setHasUnsavedChanges(true);
          message.showToast('Image translated successfully in description!', ToastTypes.SUCCESS);

        } catch (e) {
          console.error('CKEditor translation error:', e);
          message.showToast('Failed to translate image: ' + e.message, ToastTypes.ERROR);
        } finally {
          aiBtn.style.display = 'none';
        }
      };
      document.body.appendChild(aiBtn);

      editable.addEventListener('mousemove', (e) => {
        const t = e.target;
        if (t && t.tagName === 'IMG') {
          currentImgEl = t;
          const rect = t.getBoundingClientRect();
          // Position inside the top-right corner of the image
          const btnSize = 30; // must match width/height
          aiBtn.style.left = `${window.scrollX + rect.right - btnSize - 6}px`;
          aiBtn.style.top = `${window.scrollY + rect.top + 6}px`;
          aiBtn.style.display = 'block';
        } else {
          aiBtn.style.display = 'none';
        }
      });

      // Clean up on unmount
      return () => {
        try { document.body.removeChild(aiBtn); } catch { }
      };
    } catch { }
  };

  const stripHtml = (s) => String(s || '').replace(/<[^>]*>/g, '').trim();
  const normalizeBulletPoints = (raw) => {
    try {
      if (!raw) return [];
      let list = [];
      if (Array.isArray(raw)) {
        list = raw.map((it) => typeof it === 'string' ? it : (it?.text || it?.title || it?.value || ''));
      } else if (typeof raw === 'object') {
        // Try keys like bullet_point1..5 or array-like values
        const keys = ['bullet_point1', 'bullet_point2', 'bullet_point3', 'bullet_point4', 'bullet_point5'];
        list = keys.map((k) => raw[k]).filter(Boolean);
        if (!list.length) {
          // Fallback: take first 5 string values from object
          list = Object.values(raw).filter((v) => typeof v === 'string').slice(0, 5);
        }
      } else if (typeof raw === 'string') {
        // Split on newlines/semicolons
        list = raw.split(/\r?\n|;|\.|•|\u2022/g).map((s) => s.trim()).filter(Boolean);
      }
      // Clean and constrain
      list = list
        .map((s) => stripHtml(s).replace(/[\"\n\r]+/g, ' ').replace(/\s{2,}/g, ' ').trim())
        .filter(Boolean)
        .slice(0, 5)
        .map((s) => (s.length > 120 ? s.slice(0, 120) : s));
      // Pad to 5 with empty strings to keep legacy fields consistent
      while (list.length < 5) list.push('');
      return list;
    } catch {
      return [];
    }
  };

  const safeJsonParse = (text) => {
    try { return JSON.parse(text); } catch { }
    try {
      let t = String(text || '').trim();
      // Strip triple backtick fences and optional language tag (e.g., ```json ... ```)
      if (t.startsWith('```')) {
        const first = t.indexOf('```');
        const last = t.lastIndexOf('```');
        if (first !== -1 && last !== -1 && last > first) {
          t = t.slice(first + 3, last).trim();
          // If first line is a language tag like 'json', drop it
          if (/^json\s*$/i.test(t.split(/\r?\n/)[0] || '')) {
            t = t.split(/\r?\n/).slice(1).join('\n');
          }
        }
      }
      // Try direct parse
      try { return JSON.parse(t); } catch { }
      // Fallback: extract outermost braces
      const start = t.indexOf('{');
      const end = t.lastIndexOf('}');
      if (start >= 0 && end > start) {
        const slice = t.slice(start, end + 1);
        return JSON.parse(slice);
      }
    } catch { }
    return null;
  };

  const handleGenerateAll = async () => {
    try {
      setAiLoadingAll(true);
      const provider = String(aiProvider || 'local').toLowerCase();
      if (!(provider === 'openai' || provider === 'deepseek' || provider === 'bedrock')) {
        message.showToast('Select OpenAI, DeepSeek or Bedrock to generate all fields', ToastTypes.WARNING);
        return;
      }
      const existingHtml = (() => { try { return editorRef.current?.getData?.() || draft.description || ''; } catch { return draft.description || ''; } })();
      const variationSeed = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

      // Get prompt from database or use fallback
      const dbPrompt = aiPrompts['generate-all'];
      let prompt;
      if (dbPrompt?.userPromptTemplate) {
        prompt = dbPrompt.userPromptTemplate
          .replace(/\{\{draftData\}\}/g, JSON.stringify(draft).slice(0, 3000))
          .replace(/\{\{variationSeed\}\}/g, variationSeed)
          .replace(/\{\{title\}\}/g, draft.title || '')
          .replace(/\{\{description\}\}/g, draft.description || '')
          .replace(/\{\{category\}\}/g, selectedCategoryFullPath.map(c => c.c_name).join(' > ') || '')
          .replace(/\{\{brand\}\}/g, draft.brand || '');
      } else {
        prompt = `You are an e-commerce catalog copywriter creating Amazon marketplace–compatible content. Generate values for the fields below and return STRICT JSON with these exact keys only. No commentary or code fences.\n\nRules:\n- Title: ~250 WORDS (long-form keyword-rich), Amazon-compliant (no prohibited claims, no special characters beyond hyphen/en dash), one sentence preferred.\n- Description: ~500 words HTML (<p>, <ul>, <li>, <strong> only), no images.\n- Bullet points: EXACTLY 5 plain strings, each <120 chars, no quotes/newlines/HTML.\n- Fill every field if reasonably inferable.\n- Use clean plain strings for all non-HTML fields.\n\nFields to generate: {\n  "title": string,\n  "description_html": string,\n  "model": string,\n  "part_number": string,\n  "length": string,\n  "width": string,\n  "height": string,\n  "style_name": string,\n  "target_gender": string,\n  "age_range_description": string,\n  "material_type": string,\n  "fabric_type": string,\n  "pattern_type": string,\n  "color_map": string,\n  "size_map": string,\n  "fit_type": string,\n  "rise_style": string,\n  "lifestyle": string,\n  "number_of_pockets": number,\n  "unit_count": number,\n  "unit_count_type": string,\n  "duration": string,\n  "duration_unit_of_measure": string,\n  "department_name": string,\n  "item_type_name": string,\n  "item_length_description": string,\n  "pocket_description": string,\n  "care_instructions": string,\n  "generic_keywords": string,\n  "bullet_points": array of 5 strings\n}.\n\nDraft data: ${JSON.stringify(draft).slice(0, 3000)}\nvariation_seed: ${variationSeed}\n\nReturn ONLY the JSON object.`;
      }
      const maxTokens = dbPrompt?.maxTokens || 1400;
      const aiText = await callAI({ prompt, maxTokens, type: 'all' });
      const obj = safeJsonParse(String(aiText || ''));
      if (!obj) {
        message.showToast(`${provider.toUpperCase()} response parsing failed. Please try again.`, ToastTypes.ERROR);
        return;
      }
      const mergedDescription = composeDescriptionWithImages(String(obj.description_html || ''), existingHtml);
      const bp = normalizeBulletPoints(obj.bullet_points);
      setDraft((prev) => ({
        ...prev,
        title: obj.title || prev.title,
        description: mergedDescription || prev.description,
        model: obj.model || prev.model,
        part_number: obj.part_number || prev.part_number,
        length: obj.length || prev.length,
        width: obj.width || prev.width,
        height: obj.height || prev.height,
        style_name: obj.style_name || prev.style_name,
        target_gender: obj.target_gender || prev.target_gender,
        age_range_description: obj.age_range_description || prev.age_range_description,
        material_type: obj.material_type || prev.material_type,
        fabric_type: obj.fabric_type || prev.fabric_type,
        pattern_type: obj.pattern_type || prev.pattern_type,
        color_map: obj.color_map || prev.color_map,
        size_map: obj.size_map || prev.size_map,
        fit_type: obj.fit_type || prev.fit_type,
        rise_style: obj.rise_style || prev.rise_style,
        lifestyle: obj.lifestyle || prev.lifestyle,
        number_of_pockets: (typeof obj.number_of_pockets === 'number' ? obj.number_of_pockets : prev.number_of_pockets),
        unit_count: (typeof obj.unit_count === 'number' ? obj.unit_count : prev.unit_count),
        unit_count_type: obj.unit_count_type || prev.unit_count_type,
        duration: obj.duration || prev.duration,
        duration_unit_of_measure: obj.duration_unit_of_measure || prev.duration_unit_of_measure,
        department_name: obj.department_name || prev.department_name,
        item_type_name: obj.item_type_name || prev.item_type_name,
        item_length_description: obj.item_length_description || prev.item_length_description,
        pocket_description: obj.pocket_description || prev.pocket_description,
        care_instructions: obj.care_instructions || prev.care_instructions,
        generic_keywords: obj.generic_keywords || prev.generic_keywords,
        bullet_points: bp.slice(0, 5),
        bullet_point1: bp[0] || prev.bullet_point1 || '',
        bullet_point2: bp[1] || prev.bullet_point2 || '',
        bullet_point3: bp[2] || prev.bullet_point3 || '',
        bullet_point4: bp[3] || prev.bullet_point4 || '',
        bullet_point5: bp[4] || prev.bullet_point5 || '',
      }));
      try { editorRef.current?.setData?.(mergedDescription); ensureEditorImagesSafe(); } catch { }
      setHasUnsavedChanges(true);
      message.showToast('AI filled all fields', ToastTypes.SUCCESS);
    } catch (e) {
      const msg = String(e?.message || 'AI generate all failed');
      message.showToast(msg, /timeout/i.test(msg) ? ToastTypes.WARNING : ToastTypes.ERROR);
    } finally {
      setAiLoadingAll(false);
    }
  };

  useEffect(() => {
    // First fetch categories, then fetch draft data
    fetchDraftData();
    fetchCategories();
    // Fetch AI prompts and providers for this page
    fetchAIPrompts();
    fetchAIStatus();
  }, [id]);

  // Fetch AI prompts from database for product-draft-edit page
  const fetchAIPrompts = async () => {
    try {
      const res = await AIModelService.getAllPrompts({ page: 'product-draft-edit', isActive: true });
      if (res?.status?.code === 200 && res.data?.prompts) {
        const promptsMap = {};
        res.data.prompts.forEach(p => {
          promptsMap[p.action] = p;
        });
        setAiPrompts(promptsMap);
      }
    } catch (err) {
      console.error('Failed to fetch AI prompts:', err);
    }
  };

  // Fetch AI status (available providers and models) from backend
  const fetchAIStatus = async () => {
    try {
      const [statusRes, modelsRes] = await Promise.all([
        AIModelService.getAIStatus(),
        AIModelService.getAllModels({ isActive: true }),
      ]);
      if (statusRes?.status?.code === 200 && statusRes.data?.providers) {
        setAiProviders(statusRes.data.providers);
        // ⚡ Auto-select first available provider if current selection is 'local' (default)
        const currentProvider = sessionStorage.getItem('ai_provider') || 'local';
        if (currentProvider === 'local') {
          const providers = statusRes.data.providers;
          if (providers.bedrock) setAiProvider('bedrock');
          else if (providers.openai) setAiProvider('openai');
          else if (providers.deepseek) setAiProvider('deepseek');
        }
      }
      if (modelsRes?.status?.code === 200 && modelsRes.data?.models) {
        setAiModels(modelsRes.data.models);
      }
    } catch (err) {
      console.error('Failed to fetch AI status:', err);
    }
  };

  // Scrollspy + shortcuts + back-to-top visibility
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible.length > 0) {
          setActiveSection(visible[0].target.id);
        }
      },
      { rootMargin: "-120px 0px -60% 0px", threshold: [0.1, 0.25, 0.5, 0.75] }
    );

    sectionIds.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    const scrollEl = contentScrollRef.current;
    const onScroll = () => {
      if (!scrollEl) return;
      setShowBackToTop(scrollEl.scrollTop > 400);
    };
    if (scrollEl) scrollEl.addEventListener("scroll", onScroll, { passive: true });

    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        handleSubmit({ preventDefault: () => { } });
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "enter") {
        e.preventDefault();
        handlePublish();
      }
    };
    window.addEventListener("keydown", onKey);

    // Warn on unsaved changes
    const beforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", beforeUnload);

    return () => {
      observer.disconnect();
      if (scrollEl) scrollEl.removeEventListener("scroll", onScroll);
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("beforeunload", beforeUnload);
    };
  }, [hasUnsavedChanges]);

  // Effect to load category path when both categories and draft are loaded
  useEffect(() => {
    if (categoriesLoaded && draft.categoryId && originalCategories.length > 0) {
      console.log("Both categories and draft loaded, loading category path");
      loadCategoryPath(draft.categoryId);
    }
  }, [categoriesLoaded, draft.categoryId, originalCategories]);

  const fetchCategories = () => {
    return new Promise((resolve, reject) => {
      // Import the category API service dynamically to avoid circular dependencies
      import("../../../../service/website/categoryApiService").then(({ CategoryApiService }) => {
        console.log("Fetching categories...");
        CategoryApiService
          .getCategories()
          .then((res) => {
            // console.log("Category API response:", res);
            if (res.status.code === 200) {
              const categories = res.data || [];
              // console.log("Categories found:", categories);
              // console.log("Sample category structure:", categories[0]);

              if (!categories || categories.length === 0) {
                console.warn("No categories found in API response");
                message.showToast("No categories available", ToastTypes.WARNING);
                setCategoryOptions([]);
                resolve();
                return;
              }

              // Build hierarchical category tree from nested structure
              const buildCategoryTree = (categories, level = 0) => {
                const tree = [];
                const prefix = "  ".repeat(level);

                categories.forEach(category => {
                  // Add current category
                  const option = {
                    value: category._id,
                    name: prefix + category.c_name,
                    label: prefix + category.c_name,
                    level: level,
                    category: category
                  };
                  tree.push(option);
                  // console.log(`Added option: ${option.name}`);

                  // Add children recursively if they exist
                  if (category.children && category.children.length > 0) {
                    const children = buildCategoryTree(category.children, level + 1);
                    tree.push(...children);
                  }
                });

                return tree;
              };

              const options = buildCategoryTree(categories);
              // console.log("Category tree options:", options);
              // console.log("Number of category options:", options.length);
              setCategoryOptions(options);

              // Store original categories for path finding
              setOriginalCategories(categories);

              // Set initial category options (root level)
              setCurrentCategoryOptions(categories);
              // console.log("Initial categories set:", categories);
              // console.log("Sample category structure:", categories[0]);
              // console.log("Sample category _id:", categories[0]?._id);

              setCategoriesLoaded(true);
              resolve();
            } else {
              console.error("Category API error:", res);
              message.showToast("Failed to load categories", ToastTypes.ERROR);
              reject(new Error("Failed to load categories"));
            }
          })
          .catch((error) => {
            console.error("Error fetching categories:", error);
            message.showToast("Failed to load categories", ToastTypes.ERROR);
            reject(error);
          });
      });
    });
  };

  // Function to find category path by category ID
  const loadCategoryPath = (categoryId) => {
    // console.log("=== loadCategoryPath called ===");
    // console.log("categoryId:", categoryId);
    // console.log("categoriesLoaded:", categoriesLoaded);
    // console.log("originalCategories length:", originalCategories.length);

    if (!categoryId || !categoriesLoaded) {
      console.log("No categoryId or categories not loaded yet");
      return;
    }

    // console.log("Loading category path for ID:", categoryId, "Type:", typeof categoryId);

    // Function to search for category in the tree
    const findCategoryPath = (categories, targetId, currentPath = []) => {
      // console.log("Searching in categories:", categories.length, "items");

      for (const category of categories) {
        const newPath = [...currentPath, category];

        // console.log("Checking category:", category.c_name, "ID:", category._id, "Type:", typeof category._id);

        // Check if this is the target category (convert to strings for comparison)
        const categoryIdStr = String(categoryId);
        const categoryId1 = String(category._id || '');
        const categoryId2 = String(category.id || '');
        const categoryId3 = String(category.c_id || '');

        if (categoryId1 === categoryIdStr || categoryId2 === categoryIdStr || categoryId3 === categoryIdStr) {
          // console.log("Found category:", category);
          // console.log("Category path:", newPath);

          // Set the category path
          setSelectedCategoryPath(newPath);
          setSelectedCategoryFullPath(newPath);

          // Set current options to show children of the parent
          if (newPath.length > 1) {
            const parent = newPath[newPath.length - 2];
            setCurrentCategoryOptions(parent.children || []);
          } else {
            setCurrentCategoryOptions(categoryOptions);
          }

          return true;
        }

        // Recursively search children
        if (category.children && category.children.length > 0) {
          // console.log("Searching children of:", category.c_name);
          if (findCategoryPath(category.children, targetId, newPath)) {
            return true;
          }
        }
      }

      return false;
    };

    // Search in the category tree - use the original categories structure
    const found = findCategoryPath(originalCategories, categoryId);

    if (!found) {
      // console.log("Category not found with ID:", categoryId);
      // Clear the path if category not found
      setSelectedCategoryPath([]);
      setSelectedCategoryFullPath([]);
    }
  };

  const fetchDraftData = () => {
    loader.showLoading(true);
    productDraftApiService
      .getProductDraftById(id)
      .then((res) => {
        loader.showLoading(false);
        if (res.status.code === 200) {
          const draftData = res.data.draft;
          // console.log("Fetched draft data:", draftData);

          // Map the fetched data to our state, handling both new and legacy field names
          const mappedDraft = {
            // Basic Information
            title: draftData.title || draftData.item_name || "",
            sku: draftData.sku || draftData.item_sku || "",
            parentSku: draftData.parentSku || "",
            brand: draftData.brand || draftData.brand_name || "",
            manufacturer: draftData.manufacturer || "",
            description: draftData.description || draftData.product_description || "",
            model: draftData.model || "",
            part_number: draftData.part_number || "",

            // Pricing
            vendor_price: draftData.vendor_price || "",
            sp: draftData.sp || "",
            mrp: draftData.mrp || "",
            consign_price: draftData.consign_price || "",

            // Inventory
            quantity: draftData.quantity || "",
            handling_days: draftData.handling_days || "",
            max_order_quantity: draftData.max_order_quantity || "",

            local_shipping: draftData.local_shipping || "",

            // Product Details
            length: draftData.length || "",
            width: draftData.width || "",
            height: draftData.height || "",
            weight: draftData.weight || "",
            warranty: draftData.warranty || "",
            gst: draftData.gst || "",
            hsn: draftData.hsn || "",

            // Images
            imgUrl: draftData.imgUrl || [],

            // Variations
            variations: draftData.variations || [],

            // Price Ranges 
            priceRanges: draftData.priceRanges || [],

            // product sale info
            productSaleInfo: draftData.productSaleInfo || {},

            // Location & Status
            location: draftData.location || "",
            status: draftData.status || "",
            is_display: draftData.is_display || "",
            is_delete: draftData.is_delete || false,

            // Categories & Tags
            categoryId: draftData.categoryId || "",
            catalogId: draftData.catalogId || [],
            tags: draftData.tags || [],
            channel: draftData.channel || [],

            // Product Specific Fields
            feed_product_type: draftData.feed_product_type || "",
            care_instructions: draftData.care_instructions || "",
            bottoms_body_type: draftData.bottoms_body_type || "",
            target_gender: draftData.target_gender || "",
            age_range_description: draftData.age_range_description || "",
            bottoms_size_system: draftData.bottoms_size_system || "",
            bottoms_size_class: draftData.bottoms_size_class || "",
            bottoms_size: draftData.bottoms_size || "",
            bullet_points: draftData.bullet_points || [],
            generic_keywords: draftData.generic_keywords || "",
            style_name: draftData.style_name || "",
            packer_contact_information: draftData.packer_contact_information || "",
            item_type_name: draftData.item_type_name || "",
            occasion_type: draftData.occasion_type || [],
            duration_unit_of_measure: draftData.duration_unit_of_measure || "",
            sport_type: draftData.sport_type || "",
            color_map: draftData.color_map || "",
            size_map: draftData.size_map || "",
            pattern_type: draftData.pattern_type || "",
            material_type: draftData.material_type || "",
            duration: draftData.duration || "",
            rise_style: draftData.rise_style || "",
            lifestyle: draftData.lifestyle || "",
            number_of_pockets: draftData.number_of_pockets || "",
            manufacturer_contact_information: draftData.manufacturer_contact_information || "",
            fit_type: draftData.fit_type || "",
            pocket_description: draftData.pocket_description || "",
            department_name: draftData.department_name || "",
            unit_count: draftData.unit_count || "",
            item_length_description: draftData.item_length_description || "",
            unit_count_type: draftData.unit_count_type || "",
            country_of_origin: draftData.country_of_origin || "",
            fabric_type: draftData.fabric_type || "",
            fulfillment_latency: draftData.fulfillment_latency || "",
            number_of_items: draftData.number_of_items || "",

            // Draft Specific Fields
            offerId: draftData.offerId || "",
            source: draftData.source || "",
            processingStatus: draftData.processingStatus || "",
            originalData: draftData.originalData || {},

            // AI Generated Content
            aiGenerated: draftData.aiGenerated || {
              title: "",
              description: "",
              keywords: "",
              bullet_points: [],
              generatedAt: new Date()
            },

            // Search Terms
            search_terms: draftData.search_terms || [],
            target_audience: draftData.target_audience || [],

            // Legacy fields for backward compatibility
            item_name: draftData.item_name || draftData.title || "",
            item_sku: draftData.item_sku || draftData.sku || "",
            brand_name: draftData.brand_name || draftData.brand || "",
            product_description: draftData.product_description || draftData.description || "",
            standard_price: draftData.standard_price || draftData.vendor_price || "",
            sale_price: draftData.sale_price || draftData.sp || "",
            main_image_url: draftData.main_image_url || (draftData.imgUrl && draftData.imgUrl[0]) || "",
            other_image_url1: draftData.other_image_url1 || (draftData.imgUrl && draftData.imgUrl[1]) || "",
            other_image_url2: draftData.other_image_url2 || (draftData.imgUrl && draftData.imgUrl[2]) || "",
            other_image_url3: draftData.other_image_url3 || (draftData.imgUrl && draftData.imgUrl[3]) || "",
            other_image_url4: draftData.other_image_url4 || (draftData.imgUrl && draftData.imgUrl[4]) || "",
            other_image_url5: draftData.other_image_url5 || (draftData.imgUrl && draftData.imgUrl[5]) || "",
            other_image_url6: draftData.other_image_url6 || (draftData.imgUrl && draftData.imgUrl[6]) || "",
            other_image_url7: draftData.other_image_url7 || (draftData.imgUrl && draftData.imgUrl[7]) || "",
            other_image_url8: draftData.other_image_url8 || (draftData.imgUrl && draftData.imgUrl[8]) || "",
            bullet_point1: draftData.bullet_point1 || (draftData.bullet_points && draftData.bullet_points[0]) || "",
            bullet_point2: draftData.bullet_point2 || (draftData.bullet_points && draftData.bullet_points[1]) || "",
            bullet_point3: draftData.bullet_point3 || (draftData.bullet_points && draftData.bullet_points[2]) || "",
            bullet_point4: draftData.bullet_point4 || (draftData.bullet_points && draftData.bullet_points[3]) || "",
            bullet_point5: draftData.bullet_point5 || (draftData.bullet_points && draftData.bullet_points[4]) || "",
            condition_type: draftData.condition_type || "",
            condition_note: draftData.condition_note || "",
            currency: draftData.currency || "",
            offering_can_be_giftwrapped: draftData.offering_can_be_giftwrapped || false,
            offering_can_be_gift_messaged: draftData.offering_can_be_gift_messaged || false,
            recommended_browse_nodes: draftData.recommended_browse_nodes || "",
          };

          // Update SP and MRP based on minimum variant price
          const minPrice = getMinVariantPrice(mappedDraft.variations);
          if (minPrice != null && minPrice > 0) {
            mappedDraft.sp = minPrice;
            mappedDraft.mrp = Number((minPrice * 2.5).toFixed(2));
          }

          setDraft(mappedDraft);
          originalDataRef.current = mappedDraft; // Store original for change detection
          // Store original variants for reset on tab switch
          originalVariantsRef.current = Array.isArray(mappedDraft?.variations)
            ? mappedDraft.variations.map(v => ({ ...v }))
            : [];
          fetchPriceLogs(id, mappedDraft?.offerId)
          // Initialize variant selection map (default select all)
          // Also initialize baseFinalPriceRef and baseWholesalePriceRef with current saved price values
          try {
            const selection = {};
            const baseFinalPriceMap = {};
            const baseWholesalePriceMap = {};
            const flatVariants = Array.isArray(mappedDraft.variations) ? mappedDraft.variations : [];
            flatVariants.forEach((v, idx) => {
              const key = v.sku || v.skuId || v.specId || `${v.color || ''}-${v.size || ''}` || JSON.stringify(v).slice(0, 50) || String(idx);
              selection[key] = true;
              // Store current finalPrice as base (latest saved value) for dropshipping
              if (v.finalPrice != null) {
                baseFinalPriceMap[key] = v.finalPrice;
              }
              // Store current wholesaleFinalPrice as base (latest saved value) for wholesale
              if (v.wholesaleFinalPrice != null) {
                baseWholesalePriceMap[key] = v.wholesaleFinalPrice;
              }
            });
            setVariantSelection(selection);
            baseFinalPriceRef.current = baseFinalPriceMap;
            baseWholesalePriceRef.current = baseWholesalePriceMap;
          } catch { }
          setEditorReady(true);

          // Category path will be loaded by useEffect when both categories and draft are ready
        } else if (res.status.code === 402) {
          navigate("/login");
        } else {
          message.showToast(res.error.message, ToastTypes.ERROR);
        }
      })
      .catch((err) => {
        message.showToast(err?.message || "Server error. Try again later", ToastTypes.ERROR);
        loader.showLoading(false);
      });
  };

  // Ensure editor reflects latest description after async fetch
  useEffect(() => {
    if (editorRef.current) {
      try {
        const html = transformDescriptionForProd(transformDescriptionForEditor(draft.description || ""));
        editorRef.current.setData(html);
        ensureEditorImagesSafe();
      } catch { }
    }
  }, [draft.description]);

  const handleCategoryClick = (category) => {
    // console.log("Category clicked:", category.c_name);
    // console.log("Category children:", category.children);

    if (category.children && category.children.length > 0) {
      // Navigate to children
      // console.log("Navigating to children:", category.children);
      setCurrentCategoryOptions(category.children);
      setSelectedCategoryPath([...selectedCategoryPath, category]);
    } else {
      console.log("No children found, this is a leaf category");
    }
  };

  const navigateCategoryUp = () => {
    // console.log("Navigating up from path:", selectedCategoryPath);

    if (selectedCategoryPath.length > 0) {
      const newPath = selectedCategoryPath.slice(0, -1);
      setSelectedCategoryPath(newPath);

      if (newPath.length === 0) {
        // Back to root
        console.log("Going back to root categories");
        setCurrentCategoryOptions(categoryOptions);
      } else {
        // Back to parent
        // console.log("Going back to parent:", newPath[newPath.length - 1].c_name);
        setCurrentCategoryOptions(newPath[newPath.length - 1].children);
      }
    }
  };

  const selectCategory = (category) => {
    // Build the full path for display
    const fullPath = [...selectedCategoryPath, category];
    const pathString = fullPath.map(cat => cat.c_name).join(' > ');

    // console.log('Selected category:', category);
    // console.log('Full path:', pathString);
    // console.log('Category ID:', category._id);
    // console.log('Category object:', category);
    // console.log('Available fields:', Object.keys(category));

    // Check if category has an _id field
    if (!category._id) {
      console.error('Category does not have _id field:', category);
      // console.log('Available fields:', Object.keys(category));

      // Try alternative ID fields
      const categoryId = category._id || category.id || category.c_id;
      if (!categoryId) {
        message.showToast('Error: Category ID not found', ToastTypes.ERROR);
        return;
      }
      // console.log('Using alternative ID field:', categoryId);
    }

    const categoryId = category._id || category.id || category.c_id;

    // Update the draft with the selected category ID
    setDraft(prevDraft => {
      const updatedDraft = {
        ...prevDraft,
        categoryId: categoryId
      };
      // console.log('Updated draft:', updatedDraft);
      return updatedDraft;
    });

    // Store the full path for display
    setSelectedCategoryFullPath(fullPath);

    // Show success message
    message.showToast(`Category selected: ${pathString}`, ToastTypes.SUCCESS);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'categoryId') {
      // Find the selected category and get its full path
      const selectedCategory = categoryOptions.find(option => option.value === value);
      if (selectedCategory) {
        // console.log('Selected category:', selectedCategory.category);
        // console.log('Category path:', selectedCategory.name);
        // console.log('Category level:', selectedCategory.level);
      }
    }

    setDraft((prev) => {
      const next = {
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      };

      // ONLY when SP changes → auto update MRP
      if (name === "sp") {
        const spNum = Number(value || 0);
        next.mrp = Number((spNum * 2.5).toFixed(2));
      }
      return next;
    });
    setHasUnsavedChanges(true);
  };

  const toggleVariantSelection = (variant) => {
    const key = variant?.sku || variant?.skuId || variant?.specId || `${variant?.color || ''}-${variant?.size || ''}`;
    if (!key) return;
    setVariantSelection(prev => ({ ...prev, [key]: !prev[key] }));
    setHasUnsavedChanges(true);
  };

  const setAllVariants = (checked) => {
    setVariantSelection(prev => {
      const updated = { ...prev };
      (draft.variations || []).forEach(v => {
        const key = v?.sku || v?.skuId || v?.specId || `${v?.color || ''}-${v?.size || ''}`;
        if (key) updated[key] = checked;
      });
      return updated;
    });
    setHasUnsavedChanges(true);
  };

  const handleArrayInputChange = (fieldName, value) => {
    // Handle array fields like tags, search_terms, etc.
    const arrayValue = value.split(',').map(item => item.trim()).filter(item => item);
    setDraft({
      ...draft,
      [fieldName]: arrayValue,
    });
    setHasUnsavedChanges(true);
  };

  const handleVariationChange = (index, field, value) => {
    const updatedVariations = [...draft.variations];
    const currentVar = updatedVariations[index];
    const variantKey = getVarKey(currentVar, index);

    // Store old percentage value BEFORE updating (to detect unsaved changes)
    const oldPercentage = currentVar.percentage;

    const normalize = (v) => v === "" ? "" : (isNaN(v) ? v : Number(v));

    // Handle pricing mode-specific fields
    if (pricingMode === "wholesale") {
      if (field === "wholesalePercentage" || field === "wholesaleTargetPrice" || field === "wholesaleFinalPrice") {
        // Get base wholesaleFinalPrice for wholesale mode
        // CRITICAL: Always use baseWholesalePriceRef (immutable original saved price) to prevent cumulative calculations
        // NEVER use currentVar.wholesaleFinalPrice as it may be already modified
        const variantKey = getVarKey(currentVar, index);
        let baseWholesalePrice = null;

        // Priority 1: Get from baseWholesalePriceRef (latest saved wholesaleFinalPrice, immutable)
        if (variantKey && baseWholesalePriceRef.current[variantKey] != null) {
          baseWholesalePrice = baseWholesalePriceRef.current[variantKey];
        }
        // Priority 2: Fallback to originalDataRef (saved wholesaleFinalPrice from DB)
        else if (originalDataRef.current && Array.isArray(originalDataRef.current.variations)) {
          const matchedOrig = originalDataRef.current.variations.find(ov =>
            (ov.skuId && currentVar.skuId && String(ov.skuId) === String(currentVar.skuId)) ||
            (ov.specId && currentVar.specId && String(ov.specId) === String(currentVar.specId))
          );
          if (matchedOrig && matchedOrig.wholesaleFinalPrice != null) {
            baseWholesalePrice = matchedOrig.wholesaleFinalPrice;
          }
        }
        // DO NOT fallback to currentVar.wholesaleFinalPrice - it may be already modified!
        // This prevents cumulative percentage calculations

        const synced = syncVariantPricing(
          { ...currentVar, [field]: normalize(value) },
          field === "wholesalePercentage" ? "wholesalePercentage" : field === "wholesaleTargetPrice" ? "wholesaleTargetPrice" : "wholesaleFinalPrice",
          baseWholesalePrice,
          null,
          pricingMode
        );
        Object.assign(updatedVariations[index], synced);
        setDraft((prev) => ({ ...prev, variations: updatedVariations }));
        setHasUnsavedChanges(true);
        return;
      }
    }

    // Handle dropshipping mode or other fields
    if (field === "finalPrice") {
      const fp = Number(value || 0);
      updatedVariations[index].finalPriceMRP = Number((fp * 2.5).toFixed(2));
    }

    if (field.includes(".")) {
      const [root, child] = field.split(".");
      if (!updatedVariations[index][root]) updatedVariations[index][root] = {};
      updatedVariations[index][root][child] = normalize(value); // Update the nested field
    } else {
      updatedVariations[index][field] = normalize(value); // Handle simple fields
    }

    // Get base finalPrice for percentage calculations
    // Priority: latest saved finalPrice (baseFinalPriceRef) > current finalPrice from DB (originalDataRef)
    let baseFinalPrice = null;
    let originalFinalPrice = null; // Fallback for unsaved changes

    // Get latest saved finalPrice from baseFinalPriceRef (this is updated after save)
    if (baseFinalPriceRef.current[variantKey] != null) {
      baseFinalPrice = baseFinalPriceRef.current[variantKey];
    }

    // If baseFinalPrice not found, get from originalDataRef (current saved value from DB)
    if (baseFinalPrice == null && originalDataRef.current && Array.isArray(originalDataRef.current.variations)) {
      const origVar = originalDataRef.current.variations[index];
      // Try to match by skuId or specId to get the correct original variant
      const matchedOrig = originalDataRef.current.variations.find(ov =>
        (ov.skuId && currentVar.skuId && String(ov.skuId) === String(currentVar.skuId)) ||
        (ov.specId && currentVar.specId && String(ov.specId) === String(currentVar.specId))
      ) || origVar;
      if (matchedOrig && matchedOrig.finalPrice != null) {
        baseFinalPrice = matchedOrig.finalPrice;
        originalFinalPrice = matchedOrig.finalPrice; // Also set as fallback
      }
    }

    // If still not found, use current finalPrice
    if (baseFinalPrice == null && currentVar.finalPrice != null) {
      baseFinalPrice = currentVar.finalPrice;
    }

    // For percentage changes: if there are unsaved changes (percentage was already set), 
    // use original base (before first unsaved change) instead of latest saved
    if (field === "percentage") {
      // Check if percentage was already set (indicating unsaved changes)
      const hadPercentage = oldPercentage != null && oldPercentage !== "";
      if (hadPercentage && originalFinalPrice != null) {
        // Use original base for unsaved changes
        baseFinalPrice = originalFinalPrice;
      }
    }

    // keep finalPrice <-> percentage <-> targetPrice in sync (for dropshipping)
    if (pricingMode === "dropshipping" && (field === "percentage" || field === "targetPrice" || field === "finalPrice")) {
      updatedVariations[index] = syncVariantPricing(updatedVariations[index], field, baseFinalPrice, originalFinalPrice, pricingMode);
    }

    // Update SP and MRP based on minimum variant price if price changed
    const updatedDraft = { ...draft, variations: updatedVariations };
    if (field === "finalPrice" || field === "percentage" || field === "targetPrice" ||
      field === "wholesaleFinalPrice" || field === "wholesalePercentage" || field === "wholesaleTargetPrice") {
      const minPrice = getMinVariantPrice(updatedVariations);
      if (minPrice != null && minPrice > 0) {
        updatedDraft.sp = minPrice;
        updatedDraft.mrp = Number((minPrice * 2.5).toFixed(2));
      }
    }

    setDraft(updatedDraft);
    setHasUnsavedChanges(true);
  };

  const getVarKey = (v, i) =>
    v?.sku || v?.skuId || v?.specId || `${v?.color || ""}-${v?.size || ""}` || String(i);

  // Calculate minimum finalPrice from all variants (based on current pricing mode)
  const getMinVariantPrice = (variations) => {
    if (!variations || !Array.isArray(variations) || variations.length === 0) return null;

    const prices = variations
      .map(v => {
        if (pricingMode === "wholesale") {
          return v.wholesaleFinalPrice != null ? Number(v.wholesaleFinalPrice) : null;
        } else {
          return v.finalPrice != null ? Number(v.finalPrice) : null;
        }
      })
      .filter(p => p != null && p > 0);

    if (prices.length === 0) return null;
    return Math.min(...prices);
  };

  const applyBulkPercentageToAllVariants = () => {
    // Create a map of latest price (from baseFinalPriceRef/baseWholesalePriceRef or current saved value) for each variant
    const latestPriceMap = {};
    (draft.variations || []).forEach((variant, idx) => {
      const key = getVarKey(variant, idx);
      if (pricingMode === "wholesale") {
        // For wholesale, use wholesaleFinalPrice
        // CRITICAL: Always use baseWholesalePriceRef (immutable original saved price) to prevent cumulative calculations
        // Priority: baseWholesalePriceRef (latest saved) > originalDataRef (saved from DB)
        // NEVER use variant.wholesaleFinalPrice as it may be already modified
        let baseWfp = null;
        if (key && baseWholesalePriceRef.current[key] != null) {
          baseWfp = baseWholesalePriceRef.current[key];
        } else if (originalDataRef.current && Array.isArray(originalDataRef.current.variations)) {
          const matchedOrig = originalDataRef.current.variations.find(ov =>
            (ov.skuId && variant.skuId && String(ov.skuId) === String(variant.skuId)) ||
            (ov.specId && variant.specId && String(ov.specId) === String(variant.specId))
          );
          if (matchedOrig && matchedOrig.wholesaleFinalPrice != null) {
            baseWfp = matchedOrig.wholesaleFinalPrice;
          }
        }
        // DO NOT fallback to variant.wholesaleFinalPrice - it may be already modified!
        if (baseWfp != null) {
          latestPriceMap[key] = baseWfp;
        }
      } else {
        // For dropshipping, use finalPrice
        // Priority: baseFinalPriceRef (latest saved) > current finalPrice from DB
        if (baseFinalPriceRef.current[key] != null) {
          latestPriceMap[key] = baseFinalPriceRef.current[key];
        } else if (variant.finalPrice != null) {
          latestPriceMap[key] = variant.finalPrice;
        }
      }
    });

    // Get original variations from originalDataRef for fallback
    const originalVariations = originalDataRef.current?.variations || null;

    const updated = applyBulkPricing({
      variations: draft.variations || [],
      getKey: getVarKey,
      mode: "percentage",
      value: bulkPercentage,
      originalVariations: originalVariations,
      firstTimePriceMap: latestPriceMap, // Pass latest price map (using firstTimePriceMap parameter name for compatibility)
      pricingMode: pricingMode,
    });
    // Recalculate SP from minimum variant finalPrice (INR)
    const minPrice = getMinVariantPrice(updated);
    const spUpdate = {};
    if (minPrice != null && minPrice > 0) {
      spUpdate.sp = minPrice;
      spUpdate.mrp = Number((minPrice * 2.5).toFixed(2));
    }
    setDraft((prev) => ({ ...prev, variations: updated, ...spUpdate }));
    setHasUnsavedChanges(true);


  };

  const applyBulkTargetPriceToAllVariants = () => {
    // Create a map of latest price (from baseFinalPriceRef or current saved value) for each variant
    const latestPriceMap = {};
    (draft.variations || []).forEach((variant, idx) => {
      const key = getVarKey(variant, idx);
      if (pricingMode === "wholesale") {
        // For wholesale, use wholesaleFinalPrice
        // CRITICAL: Always use baseWholesalePriceRef (immutable original saved price) to prevent cumulative calculations
        // Priority: baseWholesalePriceRef (latest saved) > originalDataRef (saved from DB)
        // NEVER use variant.wholesaleFinalPrice as it may be already modified
        let baseWfp = null;
        if (key && baseWholesalePriceRef.current[key] != null) {
          baseWfp = baseWholesalePriceRef.current[key];
        } else if (originalDataRef.current && Array.isArray(originalDataRef.current.variations)) {
          const matchedOrig = originalDataRef.current.variations.find(ov =>
            (ov.skuId && variant.skuId && String(ov.skuId) === String(variant.skuId)) ||
            (ov.specId && variant.specId && String(ov.specId) === String(variant.specId))
          );
          if (matchedOrig && matchedOrig.wholesaleFinalPrice != null) {
            baseWfp = matchedOrig.wholesaleFinalPrice;
          }
        }
        // DO NOT fallback to variant.wholesaleFinalPrice - it may be already modified!
        if (baseWfp != null) {
          latestPriceMap[key] = baseWfp;
        }
      } else {
        // For dropshipping, use finalPrice
        if (baseFinalPriceRef.current[key] != null) {
          latestPriceMap[key] = baseFinalPriceRef.current[key];
        } else if (variant.finalPrice != null) {
          latestPriceMap[key] = variant.finalPrice;
        }
      }
    });

    // Get original variations from originalDataRef for fallback
    const originalVariations = originalDataRef.current?.variations || null;

    const updated = applyBulkPricing({
      variations: draft.variations || [],
      getKey: getVarKey,
      mode: "targetPrice",
      value: bulkTargetPrice,
      originalVariations: originalVariations,
      firstTimePriceMap: latestPriceMap, // Pass latest price map (using firstTimePriceMap parameter name for compatibility)
      pricingMode: pricingMode, // Pass pricing mode to handle wholesale vs dropshipping
    });
    // Recalculate SP from minimum variant finalPrice (INR)
    const minPrice = getMinVariantPrice(updated);
    const spUpdate = {};
    if (minPrice != null && minPrice > 0) {
      spUpdate.sp = minPrice;
      spUpdate.mrp = Number((minPrice * 2.5).toFixed(2));
    }
    setDraft((prev) => ({ ...prev, variations: updated, ...spUpdate }));
    setHasUnsavedChanges(true);


  };


  // PriceRange auto-calc (debounced) when RMB price changes
  const priceRangeCalcRef = useRef({ timers: {}, reqSeq: {} });

  const calculateFinalPriceFromRmb = async (apRmb) => {
    const ap = Number(apRmb || 0);
    if (!ap || ap <= 0) return "";

    // keep same algo flow used in VariantPriceModal (API)
    const payload = {
      ap: ap,
      ls: Number(draft?.local_shipping || draft?.localShipping || draft?.ls || 0), // RMB local shipping (if you have it on product)
      hsn: false,
      dw: 0.5,
      vw: 0.5,
      hsnTaxPercent: 12,
      categoryId: draft?.categoryId || "",
      sku: draft?.sku || draft?.item_sku || "",
    };

    const response = await adminApiService.post("/pricing-algorithms/calculate", payload);
    const breakdown = response?.data?.breakdown || response?.breakdown;
    const fbp = Number(breakdown?.FBP || 0);

    return fbp > 0 ? String(Math.round(fbp)) : "";
  };

  // Handler for priceRanges changes
  const handlePriceRangesChange = (field, value, index) => {
    const updatedPriceRanges = [...(draft.priceRanges || [])];

    if (!updatedPriceRanges[index]) {
      updatedPriceRanges[index] = {
        startQuantity: 0,
        price: "",
        finalPrice: "",
        id: `priceRange_${Date.now()}_${index}`,
      };
    }

    updatedPriceRanges[index] = {
      ...updatedPriceRanges[index],
      [field]: value,
    };

    setDraft({ ...draft, priceRanges: updatedPriceRanges });
    setHasUnsavedChanges(true);

    //  Auto calculate when RMB price changes
    if (field === "price") {
      const timers = priceRangeCalcRef.current.timers;
      const reqSeq = priceRangeCalcRef.current.reqSeq;

      reqSeq[index] = (reqSeq[index] || 0) + 1;
      const currentSeq = reqSeq[index];

      if (timers[index]) clearTimeout(timers[index]);

      timers[index] = setTimeout(async () => {
        const ap = Number(value || 0);
        if (!ap || ap <= 0) {
          setPriceRangeCalcOpen(false);
          setPriceRangeCalcResult(null);
          setPriceRangeCalcError("");
          return;
        }

        // Open drawer instantly (shows loading)
        setPriceRangeCalcOpen(true);
        setPriceRangeCalcMeta({ index, ap });
        setPriceRangeCalcLoading(true);
        setPriceRangeCalcError("");

        try {
          // Same endpoint used in VariantPriceModal
          const hasDims =
            Number(draft.length || 0) > 0 &&
            Number(draft.width || 0) > 0 &&
            Number(draft.height || 0) > 0;

          const deadWeightKg = Number(draft.weight || 0) > 0 ? Number(draft.weight || 0) / 1000 : 0.5;
          const volumetricWeightKg = hasDims
            ? (Number(draft.length) * Number(draft.width) * Number(draft.height)) / 5000
            : 0.5;

          const payload = {
            ap: ap,
            ls: Number(draft.local_shipping || 0), // RMB
            hsn: false,
            dw: deadWeightKg,
            vw: volumetricWeightKg,
            hsnTaxPercent: 12,
            categoryId: draft.categoryId || "",
            sku: draft?.sku || draft?.item_sku || "",
          };

          const response = await adminApiService.post("/pricing-algorithms/calculate", payload);

          // ignore if a newer request happened
          if (priceRangeCalcRef.current.reqSeq[index] !== currentSeq) return;

          const breakdown = response?.data?.breakdown || response?.breakdown;
          const algorithm = response?.data?.algorithm || response?.algorithm || "";

          if (!breakdown) throw new Error("Invalid response: breakdown not found");

          const fbp = Number(breakdown.FBP || 0);
          const finalPrice = fbp > 0 ? String(Math.round(fbp)) : "";

          // update finalPrice in draft
          setDraft((prev) => {
            const nextRanges = [...(prev.priceRanges || [])];
            if (!nextRanges[index]) return prev;

            nextRanges[index] = {
              ...nextRanges[index],
              finalPrice,
            };
            return { ...prev, priceRanges: nextRanges };
          });

          setHasUnsavedChanges(true);

          // show breakdown in drawer
          setPriceRangeCalcResult({
            algorithm,
            breakdown: {
              ...breakdown,
              // useful computed weights (same as modal concept)
              deadWeightKg: Math.round(deadWeightKg * 1000) / 1000,
              volumetricWeightKg: Math.round(volumetricWeightKg * 1000) / 1000,
            },
          });
        } catch (e) {
          const msg =
            e?.response?.data?.message ||
            e?.response?.data?.error ||
            e?.message ||
            "Failed to calculate price";

          setPriceRangeCalcError(msg);
          setPriceRangeCalcResult(null);
        } finally {
          setPriceRangeCalcLoading(false);
        }
      }, 350);
    }
  };

  // Handler for productSaleInfo changes
  const handleProductSaleInfoChange = (field, value, index) => {
    const updatedProductSaleInfo = { ...draft.productSaleInfo };

    if (field.includes(".")) {
      const fields = field.split(".");
      const mainField = fields[0]; // priceRangeList
      const nestedField = fields[1]; // startQuantity, price, promotionPrice

      // Ensure proper handling of the priceRangeList array and its elements
      if (mainField === "priceRangeList") {
        // Create a copy of the priceRangeList
        const updatedPriceRangeList = [...updatedProductSaleInfo[mainField]];
        updatedPriceRangeList[index] = {
          ...updatedPriceRangeList[index],
          [nestedField]: value,
        };

        // Update the productSaleInfo with the new priceRangeList
        updatedProductSaleInfo[mainField] = updatedPriceRangeList;
      }
    } else {
      updatedProductSaleInfo[field] = value;
    }

    // Set the updated product sale info back into the draft
    setDraft({ ...draft, productSaleInfo: updatedProductSaleInfo });
    setHasUnsavedChanges(true); // Mark the draft as unsaved
  };

  // Selected variations (only those whose checkbox is checked)
  const selectedVariations = Array.isArray(draft.variations)
    ? draft.variations.filter(v => {
      const key = v?.sku || v?.skuId || v?.specId || `${v?.color || ''}-${v?.size || ''}`;
      return key ? !!variantSelection[key] : false;
    })
    : [];


  const doUpdateDraft = (remark = "", updatedSp = null, updatedMrp = null) => {
    setLoading(true);
    loader.showLoading(true);

    // Prepare the update data, mapping back to the database structure
    const spToUse = updatedSp != null ? updatedSp : (Number(draft.sp) || 0);
    const mrpToUse = updatedMrp != null ? updatedMrp : Number((spToUse * 2.5).toFixed(2));

    const updateData = {
      // Basic Information
      title: draft.title,
      sku: draft.sku,
      parentSku: draft.parentSku,
      brand: draft.brand,
      manufacturer: draft.manufacturer,
      // Use draft.description which contains actual CloudFront URLs (not proxy URLs from editor display)
      description: draft.description,
      product_description: draft.description,
      model: draft.model,
      part_number: draft.part_number,

      // Pricing
      vendor_price: Number(draft.vendor_price) || 0,
      sp: spToUse,
      mrp: mrpToUse,
      consign_price: Number(draft.consign_price) || 0,

      // Inventory
      quantity: Number(draft.quantity) || 0,
      handling_days: Number(draft.handling_days) || 0,
      max_order_quantity: Number(draft.max_order_quantity) || 0,

      local_shipping: draft.local_shipping,

      // Product Details
      length: draft.length,
      width: draft.width,
      height: draft.height,
      weight: Number(draft.weight) || 0,
      warranty: draft.warranty,
      gst: draft.gst,
      hsn: draft.hsn,

      // Images - use imgUrl array directly (already contains translated URLs)
      imgUrl: (draft.imgUrl || []).filter(url => url && url.trim()),
      // Also send individual fields for backward compatibility
      main_image_url: draft.imgUrl?.[0] || draft.main_image_url || '',
      other_image_url1: draft.imgUrl?.[1] || '',
      other_image_url2: draft.imgUrl?.[2] || '',
      other_image_url3: draft.imgUrl?.[3] || '',
      other_image_url4: draft.imgUrl?.[4] || '',
      other_image_url5: draft.imgUrl?.[5] || '',
      other_image_url6: draft.imgUrl?.[6] || '',
      other_image_url7: draft.imgUrl?.[7] || '',
      other_image_url8: draft.imgUrl?.[8] || '',

      // Location & Status
      location: draft.location,
      is_display: draft.is_display,
      is_delete: draft.is_delete,

      // Categories & Tags
      categoryId: draft.categoryId,
      catalogId: draft.catalogId,
      tags: draft.tags,
      channel: draft.channel,

      // Product Specific Fields
      feed_product_type: draft.feed_product_type,
      care_instructions: draft.care_instructions,
      bottoms_body_type: draft.bottoms_body_type,
      target_gender: draft.target_gender,
      age_range_description: draft.age_range_description,
      bottoms_size_system: draft.bottoms_size_system,
      bottoms_size_class: draft.bottoms_size_class,
      bottoms_size: draft.bottoms_size,
      bullet_points: [
        draft.bullet_point1,
        draft.bullet_point2,
        draft.bullet_point3,
        draft.bullet_point4,
        draft.bullet_point5,
      ].filter(point => point && point.trim()),
      generic_keywords: draft.generic_keywords,
      style_name: draft.style_name,
      packer_contact_information: draft.packer_contact_information,
      item_type_name: draft.item_type_name,
      occasion_type: draft.occasion_type,
      duration_unit_of_measure: draft.duration_unit_of_measure,
      sport_type: draft.sport_type,
      color_map: draft.color_map,
      size_map: draft.size_map,
      pattern_type: draft.pattern_type,
      material_type: draft.material_type,
      duration: draft.duration,
      rise_style: draft.rise_style,
      lifestyle: draft.lifestyle,
      number_of_pockets: Number(draft.number_of_pockets) || 0,
      manufacturer_contact_information: draft.manufacturer_contact_information,
      fit_type: draft.fit_type,
      pocket_description: draft.pocket_description,
      department_name: draft.department_name,
      unit_count: Number(draft.unit_count) || 0,
      item_length_description: draft.item_length_description,
      unit_count_type: draft.unit_count_type,
      country_of_origin: draft.country_of_origin,
      fabric_type: draft.fabric_type,
      fulfillment_latency: Number(draft.fulfillment_latency) || 0,
      number_of_items: Number(draft.number_of_items) || 0,

      // Draft Specific Fields
      offerId: draft.offerId,
      source: draft.source,
      processingStatus: draft.processingStatus,
      originalData: draft.originalData,

      // AI Generated Content
      aiGenerated: draft.aiGenerated,

      // Search Terms
      search_terms: draft.search_terms,
      target_audience: draft.target_audience,

      // Variations: only selected + with editable fields
      variations: selectedVariations.map((variation) => ({
        ...variation,
        skuId: variation.skuId,
        amountOnSale: variation.amountOnSale,
        cp: variation.cp || variation.consignPrice,
        weight: variation.weight,
        length: variation.length,
        height: variation.height,
        width: variation.width,
        // actualPrice: variation.actualPrice,
        ap: variation.ap,
        finalPrice: variation.finalPrice,
        finalPriceMRP: variation.finalPriceMRP,
        percentage: variation.percentage,
        targetPrice: variation.targetPrice,
        // Wholesale pricing fields
        wholesaleFinalPrice: variation.wholesaleFinalPrice,
        wholesalePercentage: variation.wholesalePercentage,
        wholesaleTargetPrice: variation.wholesaleTargetPrice,
        // price: variation.price,    
        // Include fenxiaoPriceInfo object if it exists
        fenxiaoPriceInfo: variation.fenxiaoPriceInfo || {},
      }
      )),

      // Product Sale Info (Including priceRangeList)
      productSaleInfo: {
        amountOnSale: draft.productSaleInfo?.amountOnSale || 0,
        priceRangeList: (draft.productSaleInfo?.priceRangeList || []).map((priceRange) => ({
          startQuantity: priceRange.startQuantity,
          price: priceRange.price,
          promotionPrice: priceRange.promotionPrice,
        })),
        quoteType: draft.productSaleInfo?.quoteType || 2,
        unitInfo: draft.productSaleInfo?.unitInfo || { unit: "件", transUnit: "Piece" },
        fenxiaoSaleInfo: draft.productSaleInfo?.fenxiaoSaleInfo || { onePieceFreePostage: false, startQuantity: 1 },
      },

      // Price Ranges (for PDP display when variant details not available)
      priceRanges: (draft.priceRanges || []).map((priceRange) => ({
        startQuantity: Number(priceRange.startQuantity) || 0,
        price: String(priceRange.price || ""),
        finalPrice: String(priceRange.finalPrice || ""),
        id: priceRange.id || `priceRange_${Date.now()}`,
      })),

      updatedAt: new Date(),
      remark, // product-level remark
      pricingMode, // Pass current pricing mode (dropshipping or wholesale) for price log
    };

    productDraftApiService
      .updateProductDraft(id, updateData)
      .then((res) => {
        setLoading(false);
        loader.showLoading(false);
        if (res.status.code === 200) {
          message.showToast("Product draft updated successfully", ToastTypes.SUCCESS);
          // Stay on the same page; no redirect
          fetchDraftData();
        } else {
          message.showToast(res.error.message, ToastTypes.ERROR);
        }
      })
      .catch((err) => {
        setLoading(false);
        loader.showLoading(false);
        message.showToast(err?.message || "Server error. Try again later", ToastTypes.ERROR);
      });
    setHasUnsavedChanges(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // only ask remark if SP changed OR any selected variant changed
    const needRemark = hasSpChange() || hasAnyVariantChange();
    if (needRemark) {
      openRemarkModal("PRODUCT");
      return;
    }
    doUpdateDraft(""); // no remark needed
  };

  const submitRemark = () => {
    if (loading || variantUpdating) return; // prevent double submit while API running

    const remark = String(remarkText || "").trim();
    if (!remark) {
      message.showToast("Remark is mandatory", ToastTypes.ERROR);
      return;
    }
    const ctx = remarkContextRef.current;
    closeRemarkModal();

    if (ctx.type === "VARIANT" && typeof ctx.index === "number") {
      submitVariantUpdateWithRemark(ctx.index, remark);
      return;
    }
    if (ctx.type === "PRODUCT") {
      doUpdateDraft(remark);
      return;
    }
  };

  const openRemarkModal = (type, index = null) => {
    remarkContextRef.current = { type, index };
    setRemarkText("");
    setRemarkModalOpen(true);
  };

  const closeRemarkModal = () => {
    setRemarkModalOpen(false);
    setRemarkText("");
    remarkContextRef.current = { type: null, index: null };
  };

  const getTrackFields = () => [
    "finalPrice",
    "finalPriceMRP",
    "actualPrice",
    "percentage",
    "targetPrice",
    "wholesaleFinalPrice",
    "wholesalePercentage",
    "wholesaleTargetPrice",
    "amountOnSale",
    "length",
    "width",
    "height",
    "weight",
  ];

  const findOrigVariant = (variant) => {
    const orig = originalDataRef.current;
    if (!orig || !Array.isArray(orig.variations)) return null;

    return (
      orig.variations.find((v) => {
        if (variant?.skuId && String(v.skuId) === String(variant.skuId)) return true;
        if (variant?.specId && v?.specId && String(v.specId) === String(variant.specId)) return true;
        return false;
      }) || null
    );
  };

  const hasAnyVariantChange = () => {
    const orig = originalDataRef.current;
    if (!orig) return false;

    const track = getTrackFields();
    const selectedNow = Array.isArray(draft.variations)
      ? draft.variations.filter((v) => {
        const key = v?.sku || v?.skuId || v?.specId || `${v?.color || ""}-${v?.size || ""}`;
        return key ? !!variantSelection[key] : false;
      })
      : [];

    for (const v of selectedNow) {
      const ov = findOrigVariant(v);
      if (!ov) continue;
      if (track.some((f) => (v?.[f] ?? null) !== (ov?.[f] ?? null))) return true;
    }
    return false;
  };

  const hasSpChange = () => {
    const orig = originalDataRef.current;
    if (!orig) return false;
    return (draft?.sp ?? null) !== (orig?.sp ?? null);
  };


  // Publish Now: save all fields with status=5 (live)
  const handlePublish = () => {
    setLoading(true);

    const updateData = {
      // Basic Information
      title: draft.title,
      sku: draft.sku,
      parentSku: draft.parentSku,
      brand: draft.brand,
      manufacturer: draft.manufacturer,
      // Use draft.description which contains actual CloudFront URLs (not proxy URLs from editor display)
      description: draft.description,
      product_description: draft.description,
      model: draft.model,
      part_number: draft.part_number,

      // Pricing
      vendor_price: Number(draft.vendor_price) || 0,
      sp: Number(draft.sp) || 0,
      mrp: Number((Number(draft.sp || 0) * 2.5).toFixed(2)),
      consign_price: Number(draft.consign_price) || 0,

      // Inventory
      quantity: Number(draft.quantity) || 0,
      handling_days: Number(draft.handling_days) || 0,
      max_order_quantity: Number(draft.max_order_quantity) || 0,

      local_shipping: draft.local_shipping,

      // Product Details
      length: draft.length,
      width: draft.width,
      height: draft.height,
      weight: Number(draft.weight) || 0,
      warranty: draft.warranty,
      gst: draft.gst,
      hsn: draft.hsn,

      // Images - use imgUrl array directly (already contains translated URLs)
      imgUrl: (draft.imgUrl || []).filter(url => url && url.trim()),
      // Also send individual fields for backward compatibility
      main_image_url: draft.imgUrl?.[0] || draft.main_image_url || '',
      other_image_url1: draft.imgUrl?.[1] || '',
      other_image_url2: draft.imgUrl?.[2] || '',
      other_image_url3: draft.imgUrl?.[3] || '',
      other_image_url4: draft.imgUrl?.[4] || '',
      other_image_url5: draft.imgUrl?.[5] || '',
      other_image_url6: draft.imgUrl?.[6] || '',
      other_image_url7: draft.imgUrl?.[7] || '',
      other_image_url8: draft.imgUrl?.[8] || '',

      // Location & Status
      location: draft.location,
      status: "5",
      is_display: draft.is_display,
      is_delete: draft.is_delete,

      // Categories & Tags
      categoryId: draft.categoryId,
      catalogId: draft.catalogId,
      tags: draft.tags,
      channel: draft.channel,

      // Product Specific Fields
      feed_product_type: draft.feed_product_type,
      // Variations: include only selected ones
      variations: Array.isArray(draft.variations)
        ? draft.variations.filter(v => {
          const key = v?.sku || v?.skuId || v?.specId || `${v?.color || ''}-${v?.size || ''}`;
          return key ? !!variantSelection[key] : false;
        })
        : [],
      care_instructions: draft.care_instructions,
      bottoms_body_type: draft.bottoms_body_type,
      target_gender: draft.target_gender,
      age_range_description: draft.age_range_description,
      bottoms_size_system: draft.bottoms_size_system,
      bottoms_size_class: draft.bottoms_size_class,
      bottoms_size: draft.bottoms_size,
      bullet_points: [
        draft.bullet_point1,
        draft.bullet_point2,
        draft.bullet_point3,
        draft.bullet_point4,
        draft.bullet_point5,
      ].filter(point => point && point.trim()),
      generic_keywords: draft.generic_keywords,
      style_name: draft.style_name,
      packer_contact_information: draft.packer_contact_information,
      item_type_name: draft.item_type_name,
      occasion_type: draft.occasion_type,
      duration_unit_of_measure: draft.duration_unit_of_measure,
      sport_type: draft.sport_type,
      color_map: draft.color_map,
      size_map: draft.size_map,
      pattern_type: draft.pattern_type,
      material_type: draft.material_type,
      duration: draft.duration,
      rise_style: draft.rise_style,
      lifestyle: draft.lifestyle,
      number_of_pockets: Number(draft.number_of_pockets) || 0,
      manufacturer_contact_information: draft.manufacturer_contact_information,
      fit_type: draft.fit_type,
      pocket_description: draft.pocket_description,
      department_name: draft.department_name,
      unit_count: Number(draft.unit_count) || 0,
      item_length_description: draft.item_length_description,
      unit_count_type: draft.unit_count_type,
      country_of_origin: draft.country_of_origin,
      fabric_type: draft.fabric_type,
      fulfillment_latency: Number(draft.fulfillment_latency) || 0,
      number_of_items: Number(draft.number_of_items) || 0,

      // Draft Specific Fields
      offerId: draft.offerId,
      source: draft.source,
      processingStatus: draft.processingStatus,
      originalData: draft.originalData,

      // AI Generated Content
      aiGenerated: draft.aiGenerated,

      // Search Terms
      search_terms: draft.search_terms,
      target_audience: draft.target_audience,

      // Product Sale Info
      productSaleInfo: draft.productSaleInfo || {},

      // Price Ranges
      priceRanges: (draft.priceRanges || []).map((priceRange) => ({
        startQuantity: Number(priceRange.startQuantity) || 0,
        price: String(priceRange.price || ""),
        promotionPrice: String(priceRange.promotionPrice || ""),
        finalPrice: String(priceRange.finalPrice || ""),
        id: priceRange.id || `priceRange_${Date.now()}`,
      })),

      updatedAt: new Date(),
    };

    productDraftApiService
      .updateProductDraft(id, updateData)
      .then((res) => {
        setLoading(false);
        if (res.status.code === 200) {
          message.showToast("Product published successfully", ToastTypes.SUCCESS);
          // Stay on the same page; no redirect
        } else {
          message.showToast(res.error.message, ToastTypes.ERROR);
        }
      })
      .catch((err) => {
        setLoading(false);
        message.showToast(err?.message || "Server error. Try again later", ToastTypes.ERROR);
      });
    setHasUnsavedChanges(false);
  };

  const handleCancel = () => {
    // Stay on page; could clear changes if needed
  };

  // Nav click: scroll ONLY right-side container
  const handleAnchorClick = (id) => {
    const scrollEl = contentScrollRef.current;
    if (!scrollEl) return;

    const el = scrollEl.querySelector(`#${CSS.escape(id)}`);
    if (!el) return;

    scrollEl.scrollTo({ top: el.offsetTop, behavior: "smooth" });
    setActiveSection(id);
  };

  const toggleSection = (id) => {
    setCollapsed((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const statusOptions = [
    { name: "Pending", value: "pending" },
    { name: "Processing", value: "processing" },
    { name: "Completed", value: "completed" },
    { name: "Failed", value: "failed" },
  ];

  const conditionOptions = [
    { name: "New", value: "new" },
    { name: "Used", value: "used" },
    { name: "Refurbished", value: "refurbished" },
  ];

  const currencyOptions = [
    { name: "USD", value: "USD" },
    { name: "EUR", value: "EUR" },
    { name: "GBP", value: "GBP" },
    { name: "INR", value: "INR" },
    { name: "CNY", value: "CNY" },
  ];

  const locationOptions = [
    { name: "Global", value: "global" },
    { name: "India", value: "india" },
    { name: "China", value: "china" },
    { name: "USA", value: "usa" },
  ];



  // Get provider display name and color
  const getProviderInfo = (provider) => {
    const info = {
      bedrock: { name: 'Amazon Bedrock', color: '#ff9900', bg: '#fff7ed' },
      openai: { name: 'OpenAI', color: '#10a37f', bg: '#d1fae5' },
      deepseek: { name: 'DeepSeek', color: '#6366f1', bg: '#e0e7ff' },
      gemini: { name: 'Google Gemini', color: '#4285f4', bg: '#dbeafe' },
    };
    return info[provider] || { name: provider, color: '#6b7280', bg: '#f3f4f6' };
  };

  return (
    <div className="edit-draft-container">
      <div className="edit-draft-sticky">
        <div className="edit-draft-header">
          <div className="edit-draft-title">
            <h1>Edit Product Draft</h1>
            <p>Update product draft information</p>
          </div>
          <div className="edit-draft-ai" style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'nowrap', overflowX: 'auto' }}>
            <div className="form-input" style={{ minWidth: 200 }}>
              <label style={{ display: 'block', fontSize: 12, color: '#374151', marginBottom: 4 }}>AI Provider</label>
              <select name="ai_provider" value={aiProvider} onChange={(e) => setAiProvider(e.target.value)} style={{ padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 6 }}>
                {aiProviders.bedrock && <option value="bedrock">Amazon Bedrock ✓</option>}
                {aiProviders.openai && <option value="openai">OpenAI ✓</option>}
                {/* Show unconfigured providers as disabled */}
                {!aiProviders.bedrock && <option value="bedrock" disabled>Amazon Bedrock (not configured)</option>}
                {!aiProviders.openai && <option value="openai" disabled>OpenAI (not configured)</option>}
              </select>
            </div>
            <div style={{ fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap' }}>AI Calls: <strong>{aiCalls}</strong></div>
            {(() => {
              const p = String(aiProvider || 'local').toLowerCase();
              if (p === 'local') return null;
              const providerInfo = getProviderInfo(p);
              const isConfigured = aiProviders[p];
              return (
                <div title={isConfigured ? `${providerInfo.name} is configured` : `${providerInfo.name} is not configured in backend`}
                  style={{ fontSize: 12, padding: '4px 8px', borderRadius: 6, background: isConfigured ? providerInfo.bg : '#fee2e2', color: isConfigured ? providerInfo.color : '#991b1b', whiteSpace: 'nowrap', fontWeight: 500 }}>
                  {isConfigured ? `${providerInfo.name} ✓` : 'Not Configured'}
                </div>
              );
            })()}
            <div>
              <WebButton
                type="button"
                text={aiLoadingAll ? "Generating..." : "Generate All With AI"}
                onClick={handleGenerateAll}
                className="submit-button"
                disabled={aiLoadingAll}
                style={{ whiteSpace: 'nowrap' }}
              />
            </div>
          </div>
          <div className="edit-draft-actions">
            <WebButton
              type="button"
              text="Expand All"
              onClick={expandAll}
              className="cancel-button"
            />
            <WebButton
              type="button"
              text="Collapse All"
              onClick={collapseAll}
              className="cancel-button"
            />
            <WebButton
              type="button"
              text="Cancel"
              onClick={handleCancel}
              className="cancel-button"
            />
            <WebButton
              type="button"
              text={loading ? "Publishing..." : "Publish Now"}
              onClick={handlePublish}
              disabled={loading}
              className="submit-button"
            />
            <WebButton
              type="submit"
              text={loading ? "Updating..." : hasUnsavedChanges ? "Update Draft *" : "Update Draft"}
              disabled={loading}
              className="submit-button"
              onClick={(e) => { e.preventDefault(); handleSubmit(e); }}
            />
          </div>
        </div>
        <div className="product-list-edit-subnav">
          <a href="#basic-info" className={activeSection === "basic-info" ? "product-list-active" : ""} onClick={(e) => { e.preventDefault(); handleAnchorClick("basic-info"); }}>Basic <span className={`status-badge ${isBasicComplete ? "ok" : "warn"}`}>{isBasicComplete ? "✓" : "!"}</span></a>
          <a href="#pricing-inventory" className={activeSection === "pricing-inventory" ? "product-list-active" : ""} onClick={(e) => { e.preventDefault(); handleAnchorClick("pricing-inventory"); }}>Pricing <span className={`status-badge ${isPricingComplete ? "ok" : "warn"}`}>{isPricingComplete ? "✓" : "!"}</span></a>
          <a href="#images" className={activeSection === "images" ? "product-list-active" : ""} onClick={(e) => { e.preventDefault(); handleAnchorClick("images"); }}>Images <span className={`status-badge ${isImagesComplete ? "ok" : "warn"}`}>{isImagesComplete ? "✓" : "!"}</span></a>
          <a href="#variations" className={activeSection === "variations" ? "product-list-active" : ""} onClick={(e) => { e.preventDefault(); handleAnchorClick("variations"); }}>Variations <span className={`status-badge ${isVariationsComplete ? "ok" : "warn"}`}>{isVariationsComplete ? "✓" : "!"}</span></a>
          <a href="#product-details" className={activeSection === "product-details" ? "product-list-active" : ""} onClick={(e) => { e.preventDefault(); handleAnchorClick("product-details"); }}>Details <span className={`status-badge ${isDetailsComplete ? "ok" : "warn"}`}>{isDetailsComplete ? "✓" : "!"}</span></a>
          <a href="#seo-keywords" className={activeSection === "seo-keywords" ? "product-list-active" : ""} onClick={(e) => { e.preventDefault(); handleAnchorClick("seo-keywords"); }}>SEO <span className={`status-badge ${isSeoComplete ? "ok" : "warn"}`}>{isSeoComplete ? "✓" : "!"}</span></a>
          <a href="#draft-info" className={activeSection === "draft-info" ? "product-list-active" : ""} onClick={(e) => { e.preventDefault(); handleAnchorClick("draft-info"); }}>Draft <span className={`status-badge ${isDraftInfoComplete ? "ok" : "warn"}`}>{isDraftInfoComplete ? "✓" : "!"}</span></a>
        </div>
      </div>

      <form ref={formRef} onSubmit={handleSubmit} className="edit-draft-form">
        <div className="product-list-layout-with-sidenav">
          <PriceLogPanel
            offerId={draft?.offerId}
            productId={id}
            productData={draft}
            logs={priceLogs || []}
            loading={priceLogLoading}
            onRefresh={() => fetchPriceLogs(id, draft?.offerId)}
            onSaveActual={async (payload) => {
              loader.showLoading(true);
              try {
                const res = await productDraftApiService.updateProductDraft(id, payload);
                if (res?.status?.code === 200) {
                  message.showToast("Actual prices set successfully", ToastTypes.SUCCESS);
                  return res;
                } else {
                  throw new Error(res?.error?.message || "Failed to set actual prices");
                }
              } catch (error) {
                message.showToast(error.message || "Failed to set actual prices", ToastTypes.ERROR);
                throw error;
              } finally {
                loader.showLoading(false);
              }
            }}
            afterSave={async () => {
              await fetchDraftData();
            }}
          />
          <div ref={contentScrollRef} className="product-draft-form-scroll">
            {/* Basic Information */}
            <div id="basic-info" className="form-section">
              <div className="product-list-section-header">
                <h2>Basic Information</h2>
                <button type="button" className="product-list-collapse-btn" onClick={() => toggleSection("basic-info")}>
                  {collapsed["basic-info"] ? "Expand" : "Collapse"}
                </button>
              </div>
              <div className={`product-list-section-body ${collapsed["basic-info"] ? "product-list-collapsed" : ""}`}>

                <div className="product-list-form-row">
                  <div className="form-input product-list-form-span-2" style={{ display: "flex", gap: "10px", alignItems: "flex-end" }}>
                    <div style={{ flex: 1 }}>
                      <WebInput
                        label="Product Name"
                        name="title"
                        value={draft.title}
                        onChange={handleInputChange}
                        required
                        className="form-input"
                      />
                    </div>
                  </div>
                  <WebInput
                    label="SKU"
                    name="sku"
                    value={draft.sku}
                    onChange={handleInputChange}
                    required
                    className="form-input"
                    readonly={true}
                  />
                </div>

                <div className="product-list-form-row">
                  <WebInput
                    label="Brand Name"
                    name="brand"
                    value={draft.brand}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                </div>

                <div className="product-list-form-row">
                  <WebInput
                    label="Manufacturer"
                    name="manufacturer"
                    value={draft.manufacturer}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                  <WebInput
                    label="Model"
                    name="model"
                    value={draft.model}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                </div>

                <div className="product-list-form-row">
                  <WebInput
                    label="Part Number"
                    name="part_number"
                    value={draft.part_number}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                </div>

                <div className="product-list-form-row">
                  <div className="form-input product-list-form-span-2">
                    <label>Category *</label>
                    <div className="category-tree-selector">
                      <div className="category-breadcrumb">
                        {selectedCategoryPath.length > 0 && (
                          <>
                            <span
                              className="breadcrumb-item back-btn"
                              onClick={() => navigateCategoryUp()}
                            >
                              ← {selectedCategoryPath[selectedCategoryPath.length - 2]?.c_name || 'Home'}
                            </span>
                            <span className="breadcrumb-separator"> / </span>
                          </>
                        )}
                        <span className="current-category">
                          {selectedCategoryPath.length > 0
                            ? selectedCategoryPath[selectedCategoryPath.length - 1]?.c_name
                            : 'Select Category'
                          }
                        </span>
                      </div>

                      <div className="category-options">
                        {currentCategoryOptions.length > 0 ? (
                          currentCategoryOptions.map((category, index) => (
                            <div
                              key={index}
                              className={`category-option ${category.children && category.children.length > 0 ? 'has-children' : 'selectable'}`}
                              onClick={() => handleCategoryClick(category)}
                            >
                              <span className="category-name">{category.c_name}</span>
                              {category.children && category.children.length > 0 && (
                                <span className="expand-icon">→</span>
                              )}
                              {(!category.children || category.children.length === 0) && (
                                <button
                                  type="button"
                                  className="select-category-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    selectCategory(category);
                                  }}
                                >
                                  Select
                                </button>
                              )}
                            </div>
                          ))
                        ) : (
                          <div className="no-categories">
                            <p>No categories available at this level</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="product-list-form-row">
                  <div className="selected-category-info product-list-form-span-2">
                    <label>Selected Category Path:</label>
                    <div className="category-path">
                      {selectedCategoryFullPath.length > 0
                        ? selectedCategoryFullPath.map(cat => cat.c_name).join(' > ')
                        : 'No category selected'
                      }
                    </div>
                  </div>
                </div>



                <div className="form-group">
                  <label className="form-label">Product Description</label>
                  {(
                    <CKEditor
                      key={`${id}:${(draft.description || '').length}`}
                      editor={ClassicEditor}
                      data={transformDescriptionForProd(transformDescriptionForEditor(draft.description || ""))}
                      config={{
                        htmlSupport: {
                          allow: [
                            {
                              name: 'img',
                              attributes: {
                                referrerpolicy: true,
                                crossorigin: true
                              },
                              classes: true,
                              styles: true
                            }
                          ]
                        },
                        toolbar: [
                          'heading', '|', 'bold', 'italic', 'link', 'bulletedList', 'numberedList', '|', 'outdent', 'indent', '|', 'blockQuote', 'insertTable', 'undo', 'redo'
                        ],
                        placeholder: 'Enter product description...'
                      }}
                      onReady={(editor) => {
                        editorRef.current = editor;
                        setEditorReady(true);
                        try {
                          const html = transformDescriptionForProd(transformDescriptionForEditor(draft.description || ""));
                          editor.setData(html);
                          ensureEditorImagesSafe();
                          setupCkImageAi(editor);
                        } catch { }
                      }}
                      onChange={(event, editor) => {
                        const data = editor.getData();
                        setDraft(prev => ({ ...prev, description: data }));
                        ensureEditorImagesSafe();
                      }}
                    />
                  )}
                </div>

                <div className="product-list-form-row">
                  {/* AI controls moved to header */}
                </div>

              </div>
            </div>

            {/* Pricing & Inventory */}
            <div id="pricing-inventory" className="form-section">
              <div className="product-list-section-header">
                <h2>Pricing & Inventory</h2>
                <button type="button" className="product-list-collapse-btn" onClick={() => toggleSection("pricing-inventory")}>
                  {collapsed["pricing-inventory"] ? "Expand" : "Collapse"}
                </button>
              </div>
              <div className={`section-body ${collapsed["pricing-inventory"] ? "product-list-collapsed" : ""}`}>

                <div className="product-list-form-row">
                  <WebInput
                    label="Vendor Price"
                    name="vendor_price"
                    type="number"
                    value={Number(draft.vendor_price || 0).toFixed(2)}
                    onChange={handleInputChange}
                    step="0.01"
                    className="form-input"
                    readonly={true}
                  />
                  <WebInput
                    label="Selling Price (SP)"
                    name="sp"
                    type="number"
                    value={draft.sp}
                    onChange={handleInputChange}
                    step="0.01"
                    className="form-input"
                    readonly={true}
                  />
                </div>

                <div className="product-list-form-row">
                  <WebInput
                    label="MRP"
                    name="mrp"
                    type="number"
                    value={Number(draft.mrp || 0).toFixed(2)}
                    onChange={handleInputChange}
                    step="0.01"
                    className="form-input"
                    readonly={true}
                  />
                </div>

                <div className="product-list-form-row">
                  <WebInput
                    label="Quantity"
                    name="quantity"
                    type="number"
                    value={draft.quantity}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                  <WebInput
                    label="Max Order Quantity"
                    name="max_order_quantity"
                    type="number"
                    value={draft.max_order_quantity}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                </div>

                <div className="product-list-form-row">
                  <WebInput
                    label="MOQ (Minimum Order Quantity)"
                    name="moq"
                    type="number"
                    value={draft.moq}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                </div>

                <div className="product-list-form-row">
                  <WebInput
                    label="Handling Days"
                    name="handling_days"
                    type="number"
                    value={draft.handling_days}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                  <WebInput
                    label="Fulfillment Latency"
                    name="fulfillment_latency"
                    type="number"
                    value={draft.fulfillment_latency}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                </div>
              </div>
            </div>

            {/* Product Dimensions & Weight (hidden for now) */}

            {/* <div id="dimensions-weight" className="form-section">
            <div className="product-list-section-header">
              <h2>Product Dimensions & Weight</h2>
              <button type="button" className="product-list-collapse-btn" onClick={() => toggleSection("dimensions-weight")}>
                {collapsed["dimensions-weight"] ? "Expand" : "Collapse"}
              </button>
            </div>
            <div className={`section-body ${collapsed["dimensions-weight"] ? "product-list-collapsed" : ""}`}>
            
            <div className="product-list-form-row">
              <WebInput
                label="Length"
                name="length"
                value={draft.length}
                onChange={handleInputChange}
                className="form-input"
              />
              <WebInput
                label="Width"
                name="width"
                value={draft.width}
                onChange={handleInputChange}
                className="form-input"
              />
            </div>

            <div className="product-list-form-row">
              <WebInput
                label="Height"
                name="height"
                value={draft.height}
                onChange={handleInputChange}
                className="form-input"
              />
              <WebInput
                label="Weight"
                name="weight"
                type="number"
                value={draft.weight}
                onChange={handleInputChange}
                step="0.01"
                className="form-input"
                readonly={true}
              />
            </div>

            <div className="product-list-form-row">
              <WebInput
                label="Warranty"
                name="warranty"
                value={draft.warranty}
                onChange={handleInputChange}
                className="form-input"
              />
              <WebInput
                label="GST"
                name="gst"
                value={draft.gst}
                onChange={handleInputChange}
                className="form-input"
              />
            </div>

            <div className="product-list-form-row">
              <WebInput
                label="HSN Code"
                name="hsn"
                value={draft.hsn}
                onChange={handleInputChange}
                className="form-input"
              />
              <WebInput
                label="Barcode"
                name="barcode"
                value={draft.barcode}
                onChange={handleInputChange}
                className="form-input"
              />
            </div>
            </div>
          </div> */}

            {/* Images */}
            <div id="images" className="">
              <div className="product-list-section-header">
                <h2>Images</h2>
                <button type="button" className="product-list-collapse-btn" onClick={() => toggleSection("images")}>
                  {collapsed["images"] ? "Expand" : "Collapse"}
                </button>
              </div>
              <div className={`section-body ${collapsed["images"] ? "product-list-collapsed" : ""}`}>





                {/* Main Image Section */}
                <div className="main-image-section">
                  <h3>Main Image</h3>
                  {draft.imgUrl && draft.imgUrl.length > 0 ? (
                    <div className="main-image-container">
                      <div className="main-image-display">
                        <img
                          key={draft.imgUrl[0]}
                          src={draft.imgUrl[0]}
                          alt="Main Product Image"
                          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                          className="main-image"
                          referrerPolicy="no-referrer"
                          onError={(e) => {
                            e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2Y1ZjVmNSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5JbWFnZSBOb3QgQXZhaWxhYmxlPC90ZXh0Pjwvc3ZnPg==';
                          }}
                        />
                        <div className="main-image-overlay">
                          <div className="main-image-controls">
                            <button
                              type="button"
                              onClick={() => {
                                const newImages = draft.imgUrl.filter((_, i) => i !== 0);
                                setDraft({
                                  ...draft,
                                  imgUrl: newImages
                                });
                              }}
                              className="remove-main-image-btn"
                              title="Remove Main Image"
                            >
                              Remove Main Image
                            </button>
                          </div>
                          <div className="main-image-ai-actions">
                            <button
                              type="button"
                              className="image-ai-circle"
                              title="Translate image text to English"
                              disabled={isTranslatingIndex === 0}
                              onClick={async () => {
                                try {
                                  setIsTranslatingIndex(0);
                                  message.showToast('Translating image text…', ToastTypes.INFO);

                                  const baseUrl = process.env.REACT_APP_APIUrl1688;
                                  const token = await fetchToken();
                                  const transResp = await fetch(baseUrl.replace(/\/$/, '') + '/product-image-trans', {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json', 'X-API-Token': token },
                                    body: JSON.stringify({
                                      imageUrl: draft.imgUrl[0],
                                      originalLanguage: 'zh',
                                      targetLanguage: 'en'
                                    })
                                  });

                                  const transJson = await transResp.json();
                                  // console.log('Translation response:', transJson);

                                  const translatedUrl = transJson?.result?.result?.translatedImageUrl;

                                  if (!translatedUrl) {
                                    // console.error('No translated URL found in response:', transJson);
                                    message.showToast('Failed to translate image - no URL returned', ToastTypes.ERROR);
                                    return;
                                  }

                                  // console.log('Updating main image from:', draft.imgUrl[0], 'to:', translatedUrl);

                                  // Update state with actual translated URL (not proxy)
                                  const updated = [...(draft.imgUrl || [])];
                                  updated[0] = translatedUrl;

                                  // Force state update with new object reference
                                  setDraft(prevDraft => ({
                                    ...prevDraft,
                                    imgUrl: updated,
                                    main_image_url: translatedUrl
                                  }));

                                  setHasUnsavedChanges(true);
                                  message.showToast('Main image translated successfully!', ToastTypes.SUCCESS);

                                } catch (e) {
                                  console.error('Translation error:', e);
                                  message.showToast('Failed to translate image: ' + e.message, ToastTypes.ERROR);
                                } finally {
                                  setIsTranslatingIndex(null);
                                }
                              }}
                            >
                              {isTranslatingIndex === 0 ? '...' : 'AI'}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="no-main-image">
                      <p>No main image selected. Choose an image from the gallery below.</p>
                    </div>
                  )}
                </div>

                {/* Other Images Section */}
                <div className="other-images-section">
                  <h3>Other Images</h3>
                  <div className="thumbnails-container">
                    {/* {(draft.imgUrl || []).slice(1).map((imageUrl, index) => ( */}
                    {(draft.imgUrl || []).map((imageUrl, index) => (
                      <div key={index + 1} className="image-thumbnail-wrapper">
                        <div className="image-thumbnail">
                          <img
                            key={imageUrl}
                            src={imageUrl}
                            alt={`Product ${index + 2}`}
                            style={{ width: '100px', height: '100px', objectFit: 'cover' }}
                            className="thumbnail"
                            referrerPolicy="no-referrer"
                            onError={(e) => {
                              e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iI2Y1ZjVmNSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTAiIGZpbGw9IiM5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5OL0E8L3RleHQ+PC9zdmc+';
                            }}
                          />
                          <div className="image-overlay">
                            <div className="image-controls">
                              <button
                                type="button"
                                onClick={() => {
                                  const newImages = draft.imgUrl.filter((_, i) => i !== index + 1);
                                  setDraft({
                                    ...draft,
                                    imgUrl: newImages
                                  });
                                }}
                                className="remove-image-btn"
                                title="Remove Image"
                              >
                                ×
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  const newImages = [...draft.imgUrl];
                                  // Move the clicked image to the first position
                                  const [movedImage] = newImages.splice(index + 1, 1);
                                  newImages.unshift(movedImage);

                                  // Update both imgUrl array AND main_image_url field
                                  setDraft({
                                    ...draft,
                                    imgUrl: newImages,
                                    main_image_url: movedImage
                                  });
                                  setHasUnsavedChanges(true);
                                }}
                                className="set-main-btn"
                                title="Set as Main Image"
                              >
                                Set as Main
                              </button>
                              <div className="image-ai-actions">
                                <button
                                  type="button"
                                  className="image-ai-circle"
                                  title="Translate image text to English"
                                  disabled={isTranslatingIndex === (index + 1)}
                                  onClick={async () => {
                                    try {
                                      setIsTranslatingIndex(index + 1);
                                      message.showToast('Translating image text…', ToastTypes.INFO);

                                      const baseUrl = process.env.REACT_APP_APIUrl1688;
                                      const token = await fetchToken();
                                      const transResp = await fetch(baseUrl.replace(/\/$/, '') + '/product-image-trans', {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json', 'X-API-Token': token },
                                        body: JSON.stringify({
                                          imageUrl: imageUrl,
                                          originalLanguage: 'zh',
                                          targetLanguage: 'en'
                                        })
                                      });

                                      const transJson = await transResp.json();
                                      // console.log('Translation response:', transJson);

                                      const translatedUrl = transJson?.result?.result?.translatedImageUrl;

                                      if (!translatedUrl) {
                                        // console.error('No translated URL found in response:', transJson);
                                        message.showToast('Failed to translate image - no URL returned', ToastTypes.ERROR);
                                        return;
                                      }

                                      // console.log('Updating image from:', imageUrl, 'to:', translatedUrl);

                                      // Update state with actual translated URL (not proxy)
                                      const updated = [...(draft.imgUrl || [])];
                                      updated[index + 1] = translatedUrl;

                                      // Update the corresponding legacy field
                                      const fieldKey = `other_image_url${index + 1}`;

                                      // Force state update with new object reference
                                      setDraft(prevDraft => ({
                                        ...prevDraft,
                                        imgUrl: updated,
                                        [fieldKey]: translatedUrl
                                      }));

                                      setHasUnsavedChanges(true);
                                      message.showToast('Image translated successfully!', ToastTypes.SUCCESS);

                                    } catch (e) {
                                      console.error('Translation error:', e);
                                      message.showToast('Failed to translate image: ' + e.message, ToastTypes.ERROR);
                                    } finally {
                                      setIsTranslatingIndex(null);
                                    }
                                  }}
                                >
                                  {isTranslatingIndex === (index + 1) ? '...' : 'AI'}
                                </button>
                              </div>
                            </div>
                            <div className="image-order">
                              Image {index + 2}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  {(!draft.imgUrl || draft.imgUrl.length <= 1) && (
                    <div className="no-other-images">
                      <p>No other images available.</p>
                    </div>
                  )}
                </div>

                {/* Variants Selection & Images */}
                {/* {draft.variations && draft.variations.length > 0 && (
              <div className="images-grid">
                <div className="variants-header">
                  <h3>Variants</h3>
                  <div className="variant-actions">
                    <button type="button" onClick={() => setAllVariants(true)}>Select All</button>
                    <button type="button" onClick={() => setAllVariants(false)}>Deselect All</button>
                  </div>
                </div>
                <div className="variants-list">
                  {draft.variations.map((v, idx) => {
                    const key = v?.sku || v?.skuId || v?.specId || `${v?.color || ''}-${v?.size || ''}` || String(idx);
                    // Robustly resolve a variant image URL with multiple fallbacks for production
                    const imageFromSkuAttr = (() => {
                      try {
                        // 1) Direct fields commonly seen
                        const direct = [v?.skuImageUrl, v?.imageUrl, v?.image, v?.img, v?.skuImage]
                          .find((u) => typeof u === 'string' && u.trim());
                        if (direct) return direct;
                        // 2) Images array
                        if (Array.isArray(v?.images)) {
                          const arrUrl = v.images.find((u) => typeof u === 'string' && u.trim());
                          if (arrUrl) return arrUrl;
                        }
                        // 3) Scan skuAttributes for any URL-ish value
                        if (Array.isArray(v?.skuAttributes)) {
                          for (const a of v.skuAttributes) {
                            if (!a) continue;
                            const values = Object.values(a);
                            for (const val of values) {
                              if (typeof val === 'string' && /(https?:\/\/|^\/\/)/i.test(val)) {
                                return val;
                              }
                            }
                            // Some APIs nest value in `value` field
                            const nested = a?.value;
                            if (typeof nested === 'string' && /(https?:\/\/|^\/\/)/i.test(nested)) return nested;
                          }
                        }
                      } catch {}
                      return '';
                    })();
                    const displayName = (() => {
                      if (v?.name) return v.name;
                      const parts = [];
                      if (v?.color) parts.push(v.color);
                      if (v?.size) parts.push(v.size);
                      if (parts.length) return parts.join(" / ");
                      if (Array.isArray(v?.skuAttributes)) {
                        const colorAttr = v.skuAttributes.find(a => (a?.attributeNameTrans||a?.attributeName||'').toLowerCase().includes('color'));
                        const sizeAttr = v.skuAttributes.find(a => (a?.attributeNameTrans||a?.attributeName||'').toLowerCase().includes('size'));
                        const vals = [colorAttr?.valueTrans||colorAttr?.value, sizeAttr?.valueTrans||sizeAttr?.value].filter(Boolean);
                        if (vals.length) return vals.join(" / ");
                      }
                      return v?.sku || v?.skuId || v?.specId || `Variant ${idx+1}`;
                    })();
                    const price = v?.sp || v?.price || v?.cp || v?.consignPrice || '';
                    const qty = v?.amountOnSale || v?.quantity || '';
                    return (
                      <div key={key} className="variant-row">
                        <label className="variant-select">
                          <input
                            type="checkbox"
                            checked={!!variantSelection[key]}
                            onChange={() => toggleVariantSelection(v)}
                          />
                          <span>{displayName}</span>
                        </label>
                        {imageFromSkuAttr ? (
                          <img
                            src={normalizeUrl(imageFromSkuAttr)}
                            width="60"
                            onError={(e) => handleImgError(e, 0)}
                            crossOrigin={null}
                            referrerPolicy="no-referrer"
                          />
                        ) : null}
                        <span className="variant-meta">{price ? `Price: ${Number(price).toFixed(2)}` : ''} {qty ? `Qty: ${qty}` : ''}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )} */}

              </div>
            </div>

            {variantPriceModalOpen && activeVariant && (
              <VariantPriceModal
                open={variantPriceModalOpen}
                onClose={closeVariantPriceModal}
                variation={activeVariant}
                defaultCategoryId={draft.categoryId}
                localShipping={activeVariant?.ls ?? activeVariant?.localShipping ?? draft?.local_shipping ?? ""}
                onApplyPrice={applyVariantPrice}
                onApplyDimensions={applyVariantDimensions}
                pricingMode={pricingMode}
                allVariants={draft.variations || []}
                onApplyMultipleVariants={applyMultipleVariants}
              />
            )}

            {/* Variations Section */}
            <section id="variations" className="form-section">
              <div className="product-list-variants-header">
                <h3>Variations</h3>

                <div className="product-list-variant-actions" style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <strong style={{ fontSize: 12, fontWeight: 600 }}>Update Markup</strong>

                    {bulkMarkupMode === "percentage" ? (
                      <input
                        type="number"
                        value={bulkPercentage}
                        onChange={(e) => setBulkPercentage(e.target.value)}
                        placeholder="Percentage %"
                        style={{ width: 140, height: 22, padding: "4px 8px", fontSize: 12, borderRadius: 5, border: "1px solid #ccc", }}
                      />
                    ) : (
                      <input
                        type="number"
                        value={bulkTargetPrice}
                        onChange={(e) => setBulkTargetPrice(e.target.value)}
                        placeholder="Target Price ₹"
                        style={{ width: 140, height: 22, padding: "4px 8px", fontSize: 12, borderRadius: 5, border: "1px solid #ccc", }}
                      />
                    )}

                    <button
                      type="button"
                      disabled={isBulkApplyDisabled}
                      onClick={() => {
                        if (bulkMarkupMode === "percentage") {
                          applyBulkPercentageToAllVariants();
                        } else {
                          applyBulkTargetPriceToAllVariants();
                        }
                        // setTimeout(() => {
                        //    handleSubmit({ preventDefault: () => {} });
                        // }, 0);
                      }}
                      style={{ height: 22, fontSize: 12, padding: "0 10px", opacity: isBulkApplyDisabled ? 0.5 : 1, cursor: isBulkApplyDisabled ? "not-allowed" : "pointer", }}
                    >
                      Apply to all
                    </button>
                  </div>

                  {/* Radios */}
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, cursor: "pointer", }} >
                      <input
                        type="radio"
                        name="bulkMarkupMode"
                        checked={bulkMarkupMode === "percentage"}
                        onChange={() => setBulkMarkupMode("percentage")}
                        style={{ transform: "scale(0.9)" }}
                      />
                      Via Percentage
                    </label>

                    <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, cursor: "pointer", }} >
                      <input
                        type="radio"
                        name="bulkMarkupMode"
                        checked={bulkMarkupMode === "targetPrice"}
                        onChange={() => setBulkMarkupMode("targetPrice")}
                        style={{ transform: "scale(0.9)" }}
                      />
                      Via Target Price
                    </label>
                  </div>
                  <div ref={variantBulkMenuRef} style={{ position: "relative", display: "flex", alignItems: "center" }}>
                    <button
                      type="button"
                      onClick={() => setVariantBulkMenuOpen((v) => !v)}
                      title="Bulk select options"
                      style={{ height: 22, width: 32, borderRadius: 8, border: "1px solid #e5e7eb", background: "#ffffff", display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "pointer", boxShadow: "0 1px 2px rgba(0,0,0,0.04)", }}
                    >
                      {/* icon: checklist */}
                      <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                        <path d="M9 6h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        <path d="M9 12h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        <path d="M9 18h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        <path d="M3.5 6l1.5 1.5L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M3.5 12l1.5 1.5L7.5 11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>

                    {variantBulkMenuOpen && (
                      <div onMouseDown={(e) => e.stopPropagation()} //  prevents menu close
                        style={{ position: "absolute", top: 34, right: 0, minWidth: 220, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, boxShadow: "0 10px 25px rgba(0,0,0,0.12)", padding: 6, zIndex: 50, }}
                      >

                        <button
                          type="button"
                          onClick={() => {
                            setAllVariants(true);
                            // setVariantBulkMenuOpen(false);
                          }}
                          style={{ width: "100%", display: "flex", alignItems: "flex-start", gap: 10, padding: "10px 10px", borderRadius: 8, border: "none", background: "transparent", cursor: "pointer", }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = "#f3f4f6")}
                          onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                        >
                          <input
                            type="checkbox"
                            checked={allVariantsChecked}
                            readOnly
                            onMouseDown={(e) => e.stopPropagation()}
                            style={{ marginTop: 3 }}
                          />


                          <div style={{ textAlign: "left" }}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: "#111827" }}> Keep all variants</div>
                            <div style={{ fontSize: 11, color: "#6b7280", lineHeight: 1.25 }}>Mark all as <b>active</b> (no deletion)</div>
                          </div>

                        </button>

                        <button
                          type="button"
                          onClick={() => {
                            setAllVariants(false);
                            // setVariantBulkMenuOpen(false);
                          }}
                          style={{
                            width: "100%",
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 10,
                            padding: "10px 10px",
                            borderRadius: 8,
                            border: "none",
                            background: "transparent",
                            cursor: "pointer",
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = "#fef2f2")}
                          onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                        >
                          <input
                            type="checkbox"
                            checked={allVariantsUnchecked}
                            readOnly
                            onMouseDown={(e) => e.stopPropagation()}
                            style={{ marginTop: 3 }}
                          />


                          <div style={{ textAlign: "left" }}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: "#991b1b" }}>
                              Delete all variants
                            </div>
                            <div style={{ fontSize: 11, color: "#6b7280", lineHeight: 1.25 }}>
                              Mark all as <b>delete</b> (removed from DB on save)
                            </div>
                          </div>

                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Pricing Mode Tabs - Separate Row */}
              <div style={{ display: "flex", gap: 8, marginTop: 12, marginBottom: 12, borderBottom: "1px solid #e5e7eb", paddingBottom: 8 }}>
                <button
                  type="button"
                  onClick={() => handlePricingModeChange("dropshipping")}
                  style={{
                    padding: "4px 12px",
                    height: "22px",
                    border: "none",
                    background: pricingMode === "dropshipping" ? "#2563eb" : "transparent",
                    color: pricingMode === "dropshipping" ? "#fff" : "#6b7280",
                    cursor: "pointer",
                    borderBottom: pricingMode === "dropshipping" ? "2px solid #2563eb" : "2px solid transparent",
                    fontWeight: pricingMode === "dropshipping" ? 600 : 400,
                    fontSize: 12,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  Dropshipping
                </button>
                <button
                  type="button"
                  onClick={() => handlePricingModeChange("wholesale")}
                  style={{
                    padding: "4px 12px",
                    height: "22px",
                    border: "none",
                    background: pricingMode === "wholesale" ? "#2563eb" : "transparent",
                    color: pricingMode === "wholesale" ? "#fff" : "#6b7280",
                    cursor: "pointer",
                    borderBottom: pricingMode === "wholesale" ? "2px solid #2563eb" : "2px solid transparent",
                    fontWeight: pricingMode === "wholesale" ? 600 : 400,
                    fontSize: 12,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  Wholesale
                </button>
              </div>

              {draft.variations.map((variation, index) => {
                // Define key based on the variation's properties (sku, skuId, specId)
                const key = variation?.sku || variation?.skuId || variation?.specId || `${variation?.color || ''}-${variation?.size || ''}` || String(index);

                return (
                  <div key={index} className="variation-card">
                    <div className="variation-header">
                      <div className="variation-left">
                        {variation.skuAttributes && variation.skuAttributes.length > 0 && variation.skuAttributes[0].skuImageUrl ? (
                          <img
                            src={variation.skuAttributes[0].skuImageUrl}
                            alt="Variation Image"
                            className="variation-image"
                            referrerPolicy="no-referrer"
                          />
                        ) : null}

                        <h3>
                          {variation.skuAttributes && variation.skuAttributes.length > 0
                            ? variation.skuAttributes[0].valueTrans || variation.skuAttributes[0].value
                            : "Variations"}
                        </h3>
                      </div>

                      <button
                        type="button"
                        className="variant-update-price-btn"
                        onClick={() => openVariantPriceModal(variation, index)}
                      >
                        Update Price with Algo
                      </button>

                      <label className="variant-select">
                        Uncheck to delete variant from DB
                        <input
                          type="checkbox"
                          checked={!!variantSelection[key]}
                          onChange={() => toggleVariantSelection(variation)}
                        />
                      </label>

                    </div>


                    <div className="form-row variation-form-row">
                      <div className="variation-form-input">
                        <label>sku Id</label>
                        <input
                          readOnly
                          type="number"
                          value={variation.skuId || ""}
                          onChange={(e) => handleVariationChange(index, "skuId", e.target.value)}
                        />
                      </div>
                      <div className="variation-form-input">
                        <label>Amount On Sale</label>
                        <input
                          type="number"
                          value={variation.amountOnSale || ""}
                          onChange={(e) => handleVariationChange(index, "amountOnSale", e.target.value)}
                        />
                      </div>
                      <div className="variation-form-input">
                        <label>Consign Price</label>
                        <input
                          readOnly
                          type="text"
                          value={variation.cp || ""}
                          onChange={(e) => handleVariationChange(index, "cp", e.target.value)}
                        />
                      </div>
                      <div className="variation-form-input">
                        <label>Weight (kg)</label>
                        <input
                          readOnly
                          type="number"
                          value={variation.weight ?? 0}
                          onChange={(e) => handleVariationChange(index, "weight", e.target.value === "" ? 0 : +e.target.value)}
                        />
                      </div>
                      <div className="variation-form-input">
                        <label>Length (cm)</label>
                        <input
                          readOnly
                          type="number"
                          value={variation.length ?? 0}
                          onChange={(e) => handleVariationChange(index, "length", e.target.value === "" ? 0 : +e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="form-row variation-form-row">

                      <div className="variation-form-input">
                        <label>Height (cm)</label>
                        <input
                          readOnly
                          type="number"
                          value={variation.height ?? 0}
                          onChange={(e) => handleVariationChange(index, "height", e.target.value === "" ? 0 : +e.target.value)}
                        />
                      </div>
                      <div className="variation-form-input">
                        <label>Width (cm)</label>
                        <input
                          readOnly
                          type="number"
                          value={variation.width ?? 0}
                          onChange={(e) => handleVariationChange(index, "width", e.target.value === "" ? 0 : +e.target.value)}
                        />
                      </div>
                      <div className="variation-form-input">
                        <label>Actual Price</label>
                        <input
                          readOnly
                          type="number"
                          value={variation.ap ?? variation.actualPrice ?? 0}
                          onChange={(e) => handleVariationChange(index, "ap", e.target.value)}
                        />
                      </div>
                      {pricingMode === "dropshipping" ? (
                        <>
                          <div className="variation-form-input">
                            <label>Final Price </label>
                            <input
                              readOnly
                              type="number"
                              value={variation.finalPrice || ""}
                              onChange={(e) => handleVariationChange(index, "finalPrice", e.target.value)}
                            />
                          </div>
                          <div className="variation-form-input">
                            <label>Final Price MRP</label>
                            <input
                              readOnly
                              type="number"
                              value={Number(variation.finalPriceMRP || 0).toFixed(2)}
                              onChange={(e) => handleVariationChange(index, "finalPriceMRP", e.target.value)}
                            />
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="variation-form-input">
                            <label>Wholesale Final Price </label>
                            <input
                              readOnly
                              type="number"
                              value={variation.wholesaleFinalPrice || ""}
                              onChange={(e) => handleVariationChange(index, "wholesaleFinalPrice", e.target.value)}
                            />
                          </div>
                        </>
                      )}
                    </div>
                    {/* Fenxiao Price Info */}
                    {variation.fenxiaoPriceInfo && (
                      <div className="fenxiao-price-info">
                        <div className="fenxiao-headings">
                          <h4>Fenxiao Price Info : </h4>
                          <h4>Markup Settings :</h4>
                        </div>
                        <div className="variant-price-row5">
                          {/* Fenxiao */}
                          <div className="variation-form-input">
                            <label>Offer Price</label>
                            <input
                              readOnly
                              type="text"
                            // Hidden for now because offer price is not correct from API some other calculation happens when fetch data that is why offer price is not correct but in db it is correct.
                            // value={variation.fenxiaoPriceInfo.offerPrice || ""}
                            // onChange={(e) => handleVariationChange(index, "fenxiaoPriceInfo.offerPrice", e.target.value)}
                            />
                          </div>

                          <div className="variation-form-input">
                            <label>One Piece Price</label>
                            <input
                              readOnly
                              type="text"
                              value={variation.fenxiaoPriceInfo.onePiecePrice || ""}
                              onChange={(e) => handleVariationChange(index, "fenxiaoPriceInfo.onePiecePrice", e.target.value)}
                            />
                          </div>

                          {/* RIGHT: Markup */}
                          {pricingMode === "dropshipping" ? (
                            <>
                              <div className="variation-form-input">
                                <label>Set Percentage (%)</label>
                                <input
                                  type="number"
                                  value={variation.percentage ?? ""}
                                  onChange={(e) => handleVariationChange(index, "percentage", e.target.value)}
                                />
                              </div>

                              <div className="variation-form-input">
                                <label>Set Target Price</label>
                                <input
                                  type="number"
                                  value={variation.targetPrice ?? ""}
                                  onChange={(e) => handleVariationChange(index, "targetPrice", e.target.value)}
                                />
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="variation-form-input">
                                <label>Wholesale Percentage (%)</label>
                                <input
                                  type="number"
                                  value={variation.wholesalePercentage ?? ""}
                                  onChange={(e) => handleVariationChange(index, "wholesalePercentage", e.target.value)}
                                />
                              </div>

                              <div className="variation-form-input">
                                <label>Wholesale Target Price</label>
                                <input
                                  type="number"
                                  value={variation.wholesaleTargetPrice ?? ""}
                                  onChange={(e) => handleVariationChange(index, "wholesaleTargetPrice", e.target.value)}
                                />
                              </div>
                            </>
                          )}

                          {/* Update button */}
                          <div className="variation-form-input">
                            <label style={{ visibility: "hidden" }}>Update</label>
                            <button
                              type="button"
                              className="product-list-variant-row-update-btn"
                              // onClick={() => handleSubmit({ preventDefault: () => {} })}
                              onClick={() => handleUpdateVariant(index)}
                              disabled={variantUpdating || loading}
                            >
                              {variantUpdating ? "Updating..." : "Update Variant"}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </section>

            {/* Price Ranges Section */}
            <section id="price-ranges" className="form-section">
              <div className="product-list-section-header">
                <h2>Price Ranges</h2>
                <button
                  type="button"
                  className="product-list-collapse-btn"
                  onClick={() => toggleSection("price-ranges")}
                >
                  {collapsed["price-ranges"] ? "Expand" : "Collapse"}
                </button>
              </div>
              <div className={`section-body ${collapsed["price-ranges"] ? "product-list-collapsed" : ""}`}>
                <p style={{ fontSize: "13px", color: "#2563eb", marginBottom: "16px", fontWeight: 500, backgroundColor: "#eff6ff", padding: "10px 12px", borderRadius: "6px", border: "1px solid #bfdbfe" }}>
                  When variant details are not available, update the Price Range (RMB) to auto-calculate Final Price.
                  <br />
                  <span style={{ fontSize: "12px", color: "#64748b" }}>
                    Note: Price Range edits are not saved in Price Log History.
                  </span>
                </p>

                {draft.priceRanges && draft.priceRanges.length > 0 ? (
                  <div className="price-ranges-list">
                    {draft.priceRanges.map((priceRange, index) => (
                      <div key={priceRange.id || index} className="price-range-item" style={{
                        border: "1px solid #e5e7eb",
                        borderRadius: "8px",
                        padding: "16px",
                        marginBottom: "12px",
                        backgroundColor: "#f9fafb",
                      }}>
                        <h4 style={{ fontSize: "14px", fontWeight: 600, margin: "0 0 12px 0" }}>Price Range #{index + 1}</h4>

                        <div className="form-row product-list-variation-form-row">
                          <div className="variation-form-input">
                            <label>Start Quantity</label>
                            <input
                              type="number"
                              value={priceRange.startQuantity || ""}
                              onChange={(e) => handlePriceRangesChange("startQuantity", e.target.value, index)}
                              placeholder="e.g., 1, 2, 10"
                              readOnly
                            />
                          </div>
                          <div className="variation-form-input">
                            <label>Price (RMB)</label>
                            <input
                              type="number"
                              step="0.01"
                              value={priceRange.price || ""}
                              onChange={(e) => handlePriceRangesChange("price", e.target.value, index)}
                              placeholder="Base price"
                            />
                          </div>
                          <div className="variation-form-input">
                            <label>Final Price (₹)</label>
                            <input
                              type="number"
                              step="0.01"
                              value={priceRange.finalPrice || ""}
                              onChange={(e) => handlePriceRangesChange("finalPrice", e.target.value, index)}
                              placeholder="Final calculated price in INR"
                              readOnly
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{
                    padding: "24px",
                    textAlign: "center",
                    color: "#6b7280",
                    backgroundColor: "#f9fafb",
                    borderRadius: "8px",
                    border: "1px dashed #d1d5db"
                  }}>
                    <p style={{ margin: 0, fontSize: "14px" }}>No price ranges available.</p>
                  </div>
                )}
              </div>
            </section>



            {priceRangeCalcOpen && (
              <div
                style={{
                  position: "fixed",
                  right: 16,
                  top: 210,
                  width: 380,
                  maxHeight: "calc(100vh - 110px)",
                  background: "#fff",
                  boxShadow: "0 10px 30px rgba(0,0,0,0.18)",
                  borderRadius: 12,
                  zIndex: 9999,
                  overflow: "hidden",
                  pointerEvents: "auto",
                }}
              >
                <div style={{ padding: 16, overflowY: "auto", maxHeight: "calc(100vh - 110px)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14 }}>Price Breakdown</div>
                      <div style={{ fontSize: 12, color: "#64748b" }}>
                        Row #{(priceRangeCalcMeta?.index ?? 0) + 1} • AP (RMB): {priceRangeCalcMeta?.ap}
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => setPriceRangeCalcOpen(false)}
                      style={{
                        border: "1px solid #e5e7eb",
                        borderRadius: 8,
                        padding: "2px 8px",
                        cursor: "pointer",
                        background: "#fff",
                      }}
                      aria-label="Close"
                    >
                      ✕
                    </button>
                  </div>

                  {priceRangeCalcLoading && (
                    <div style={{ fontSize: 13, color: "#475569", padding: "10px 12px", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8 }}>
                      Calculating price...
                    </div>
                  )}

                  {priceRangeCalcError && (
                    <div style={{ fontSize: 13, color: "#b91c1c", padding: "10px 12px", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8 }}>
                      {priceRangeCalcError}
                    </div>
                  )}

                  {priceRangeCalcResult?.breakdown && !priceRangeCalcLoading && (
                    <div style={{ marginTop: 2 }}>
                      <div style={{ fontSize: 12, color: "#64748b", marginBottom: 6 }}>
                        Algorithm: <b>{priceRangeCalcResult.algorithm || "---"}</b>
                      </div>

                      <div style={{ padding: 12, border: "1px solid #e5e7eb", borderRadius: 10, background: "#f9fafb" }}>
                        <div style={{ fontWeight: 800, fontSize: 16, color: "#16a34a", marginBottom: 10 }}>
                          FBP (Final Buyer Price): ₹{Math.round(Number(priceRangeCalcResult.breakdown.FBP || 0))}
                        </div>

                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, fontSize: 12, color: "#0f172a" }}>
                          <div>AP_RMB: {priceRangeCalcResult.breakdown.AP_RMB}</div>
                          <div>AP_INR: ₹{Math.round(Number(priceRangeCalcResult.breakdown.AP_INR || 0))}</div>

                          <div>MP: ₹{Math.round(Number(priceRangeCalcResult.breakdown.MP || 0))}</div>
                          <div>CP: ₹{Math.round(Number(priceRangeCalcResult.breakdown.CP || 0))}</div>

                          <div>LS: ₹{Math.round(Number(priceRangeCalcResult.breakdown.LS || 0))}</div>
                          <div>CD: ₹{Math.round(Number(priceRangeCalcResult.breakdown.CD || 0))}</div>

                          <div>IF: ₹{Math.round(Number(priceRangeCalcResult.breakdown.IF || 0))}</div>
                          <div>LMD: ₹{Math.round(Number(priceRangeCalcResult.breakdown.LMD || 0))}</div>

                          <div>LC: ₹{Math.round(Number(priceRangeCalcResult.breakdown.LC || 0))}</div>
                          <div>PF: ₹{Math.round(Number(priceRangeCalcResult.breakdown.PF || 0))}</div>

                          <div>GST_PR: ₹{Math.round(Number(priceRangeCalcResult.breakdown.GST_PR || 0))}</div>
                          <div>GST_FR: ₹{Math.round(Number(priceRangeCalcResult.breakdown.GST_FR || 0))}</div>

                          <div>GST_PF: ₹{Math.round(Number(priceRangeCalcResult.breakdown.GST_PF || 0))}</div>
                          <div>Chargeable Wt: {priceRangeCalcResult.breakdown.chargeableWeight || "-"} kg</div>
                        </div>

                        <div style={{ marginTop: 12, paddingTop: 10, borderTop: "1px dashed #e5e7eb", fontSize: 12, color: "#334155" }}>
                          <div><b>Weight Used</b></div>
                          <div>VW: {priceRangeCalcResult.breakdown.volumetricWeightKg} kg</div>
                          <div>DW: {priceRangeCalcResult.breakdown.deadWeightKg} kg</div>
                        </div>
                      </div>

                      <div style={{ marginTop: 10, fontSize: 12, color: "#64748b" }}>
                        Note: This breakdown is preview only. Save it by clicking <b>Update Draft</b>.
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* // Product Sale Info Section */}

            <section className="form-section product-saleinfo">
              <h2>Product Sale Info</h2>
              <div className="form-row product-list-variation-form-row">
                <div className="variation-form-input">
                  <label>Amount On Sale</label>
                  <input
                    type="number"
                    value={draft.productSaleInfo?.amountOnSale || ""}
                    onChange={(e) => handleProductSaleInfoChange("amountOnSale", e.target.value)}
                  />
                </div>
              </div>

              {/* Price Range List */}
              <h3>Price Range</h3>
              {draft.productSaleInfo?.priceRangeList?.map((priceRange, index) => (
                <div key={index} className="price-range-item">
                  <div className="form-row product-list-variation-form-row">
                    <div className="variation-form-input">
                      <label>Start Quantity</label>
                      <input
                        readOnly
                        type="number"
                        value={priceRange.startQuantity || ""}
                        onChange={(e) => handleProductSaleInfoChange(`priceRangeList.startQuantity`, e.target.value, index)}
                      />
                    </div>
                    <div className="variation-form-input">
                      <label>Price</label>
                      <input
                        readOnly
                        type="number"
                        value={priceRange.price || ""}
                        onChange={(e) => handleProductSaleInfoChange(`priceRangeList.price`, e.target.value, index)}
                      />
                    </div>
                    <div className="variation-form-input">
                      <label>Promotion Price</label>
                      <input
                        readOnly
                        type="text"
                        value={priceRange.promotionPrice || ""}
                        onChange={(e) => handleProductSaleInfoChange(`priceRangeList.promotionPrice`, e.target.value, index)}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </section>

            {/* Product Details */}
            <div id="product-details" className="form-section">
              <div className="product-list-section-header">
                <h2>Product Details</h2>
                <button type="button" className="product-list-collapse-btn" onClick={() => toggleSection("product-details")}>
                  {collapsed["product-details"] ? "Expand" : "Collapse"}
                </button>
              </div>
              <div className={`section-body ${collapsed["product-details"] ? "product-list-collapsed" : ""}`}>

                <div className="product-list-form-row">
                  <WebInput
                    label="Feed Product Type"
                    name="feed_product_type"
                    value={draft.feed_product_type}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                  <WebInput
                    label="Style Name"
                    name="style_name"
                    value={draft.style_name}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                </div>

                <div className="product-list-form-row">
                  <WebInput
                    label="Target Gender"
                    name="target_gender"
                    value={draft.target_gender}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                  <WebInput
                    label="Age Range Description"
                    name="age_range_description"
                    value={draft.age_range_description}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                </div>

                <div className="product-list-form-row">
                  <WebInput
                    label="Material Type"
                    name="material_type"
                    value={draft.material_type}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                  <WebInput
                    label="Fabric Type"
                    name="fabric_type"
                    value={draft.fabric_type}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                </div>

                <div className="product-list-form-row">
                  <WebInput
                    label="Pattern Type"
                    name="pattern_type"
                    value={draft.pattern_type}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                  <WebInput
                    label="Color Map"
                    name="color_map"
                    value={draft.color_map}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                </div>

                <div className="product-list-form-row">
                  <WebInput
                    label="Size Map"
                    name="size_map"
                    value={draft.size_map}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                  <WebInput
                    label="Fit Type"
                    name="fit_type"
                    value={draft.fit_type}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                </div>

                <div className="product-list-form-row">
                  <WebInput
                    label="Rise Style"
                    name="rise_style"
                    value={draft.rise_style}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                  <WebInput
                    label="Lifestyle"
                    name="lifestyle"
                    value={draft.lifestyle}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                </div>

                <div className="product-list-form-row">
                  <WebInput
                    label="Number of Pockets"
                    name="number_of_pockets"
                    type="number"
                    value={draft.number_of_pockets}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                  <WebInput
                    label="Unit Count"
                    name="unit_count"
                    type="number"
                    value={draft.unit_count}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                </div>

                <div className="product-list-form-row">
                  <WebInput
                    label="Unit Count Type"
                    name="unit_count_type"
                    value={draft.unit_count_type}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                  <WebInput
                    label="Duration"
                    name="duration"
                    value={draft.duration}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                </div>

                <div className="product-list-form-row">
                  <WebInput
                    label="Duration Unit of Measure"
                    name="duration_unit_of_measure"
                    value={draft.duration_unit_of_measure}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                  <WebInput
                    label="Country of Origin"
                    name="country_of_origin"
                    value={draft.country_of_origin}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                </div>

                <div className="product-list-form-row">
                  <WebInput
                    label="Department Name"
                    name="department_name"
                    value={draft.department_name}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                  <WebInput
                    label="Item Type Name"
                    name="item_type_name"
                    value={draft.item_type_name}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                </div>

                <div className="product-list-form-row">
                  <WebInput
                    label="Item Length Description"
                    name="item_length_description"
                    value={draft.item_length_description}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                  <WebInput
                    label="Pocket Description"
                    name="pocket_description"
                    value={draft.pocket_description}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                </div>

                <div className="product-list-form-row">
                  <WebInput
                    label="Care Instructions"
                    name="care_instructions"
                    value={draft.care_instructions}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                  <WebInput
                    label="Packer Contact Information"
                    name="packer_contact_information"
                    value={draft.packer_contact_information}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                </div>

                <div className="product-list-form-row">
                  <WebInput
                    label="Manufacturer Contact Information"
                    name="manufacturer_contact_information"
                    value={draft.manufacturer_contact_information}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                  <WebInput
                    label="Number of Items"
                    name="number_of_items"
                    type="number"
                    value={draft.number_of_items}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                </div>
              </div>
            </div>

            {/* SEO & Keywords */}
            <div id="seo-keywords" className="form-section">
              <div className="product-list-section-header">
                <h2>SEO & Keywords</h2>
                <button type="button" className="product-list-collapse-btn" onClick={() => toggleSection("seo-keywords")}>
                  {collapsed["seo-keywords"] ? "Expand" : "Collapse"}
                </button>
              </div>
              <div className={`section-body ${collapsed["seo-keywords"] ? "product-list-collapsed" : ""}`}>

                <WebInput
                  label="Generic Keywords"
                  name="generic_keywords"
                  value={draft.generic_keywords}
                  onChange={handleInputChange}
                  className="form-input"
                />

                <WebInput
                  label="Search Terms (comma separated)"
                  name="search_terms"
                  value={draft.search_terms.join(', ')}
                  onChange={(e) => handleArrayInputChange('search_terms', e.target.value)}
                  className="form-input"
                />

                <WebInput
                  label="Target Audience (comma separated)"
                  name="target_audience"
                  value={draft.target_audience.join(', ')}
                  onChange={(e) => handleArrayInputChange('target_audience', e.target.value)}
                  className="form-input"
                />

                <div className="product-list-form-row">
                  <WebInput
                    label="Bullet Point 1"
                    name="bullet_point1"
                    value={draft.bullet_point1}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                  <WebInput
                    label="Bullet Point 2"
                    name="bullet_point2"
                    value={draft.bullet_point2}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                </div>

                <div className="product-list-form-row">
                  <WebInput
                    label="Bullet Point 3"
                    name="bullet_point3"
                    value={draft.bullet_point3}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                  <WebInput
                    label="Bullet Point 4"
                    name="bullet_point4"
                    value={draft.bullet_point4}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                </div>

                <WebInput
                  label="Bullet Point 5"
                  name="bullet_point5"
                  value={draft.bullet_point5}
                  onChange={handleInputChange}
                  className="form-input"
                />
              </div>
            </div>

            {/* Draft Specific Information */}
            <div id="draft-info" className="form-section">
              <div className="product-list-section-header">
                <h2>Draft Specific Information</h2>
                <button type="button" className="product-list-collapse-btn" onClick={() => toggleSection("draft-info")}>
                  {collapsed["draft-info"] ? "Expand" : "Collapse"}
                </button>
              </div>
              <div className={`section-body ${collapsed["draft-info"] ? "product-list-collapsed" : ""}`}>

                <div className="product-list-form-row">
                  <WebInput
                    label="Offer ID"
                    name="offerId"
                    value={draft.offerId}
                    onChange={handleInputChange}
                    className="form-input"
                    disabled
                    readOnly
                  />
                  <WebInput
                    label="Source"
                    name="source"
                    value={draft.source}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                </div>

                <div className="product-list-form-row">
                  <WebDropdown
                    label="Location"
                    name="location"
                    options={locationOptions}
                    selected={draft.location}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                  <WebDropdown
                    label="Processing Status"
                    name="processingStatus"
                    options={statusOptions}
                    selected={draft.processingStatus}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                </div>

                <div className="product-list-form-row">
                  <WebInput
                    label="Is Display"
                    name="is_display"
                    value={draft.is_display}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                </div>

                <div className="product-list-form-row">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="is_delete"
                      checked={draft.is_delete}
                      onChange={handleInputChange}
                    />
                    Is Deleted
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>


      {remarkModalOpen && (
        <div className="remark-modal-backdrop" onMouseDown={closeRemarkModal}>
          <div className="remark-modal" onMouseDown={(e) => e.stopPropagation()}>
            <div className="remark-modal-head">
              <div>
                <h3 className="remark-modal-title">Add Remark</h3>
                <p className="remark-modal-sub">Remark is mandatory</p>
              </div>
              <button type="button"
                className="remark-modal-close"
                onClick={closeRemarkModal}
                disabled={loading || variantUpdating}
              >
                ×
              </button>
            </div>

            <textarea
              className="remark-modal-input"
              placeholder="Enter remark..."
              value={remarkText}
              onChange={(e) => setRemarkText(e.target.value)}
              rows={4}
              autoFocus
            />

            <div className="remark-modal-actions">
              <button type="button"
                className="remark-modal-btn ghost"
                onClick={closeRemarkModal}
                disabled={loading || variantUpdating}
              >
                Cancel
              </button>
              <button type="button"
                className="remark-modal-btn"
                onClick={submitRemark}
                disabled={loading || variantUpdating}
              >
                {(loading || variantUpdating) ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showBackToTop && (
        <button
          type="button"
          className="back-to-top"
          onClick={() => contentScrollRef.current?.scrollTo({ top: 0, behavior: "smooth" })}
        >
          ↑ Top
        </button>
      )}
    </div>
  );
}